import { Note } from '@museflow-ai/opensheetmusicdisplay';
import {
  PrerenderedGraphics,
  MidiEventState,
  midiSemiErrorStates,
  midiFullErrorStates,
  MidiEventType,
  ErrorRecognitionActive,
} from 'Types';
import {
  makeNoteSemiError,
  makeNoteError,
  makeNoteCorrect,
  resetNote,
  reachedTieHeads,
} from 'Utils/OSMDUtility';
import { chordHeadRankAscending } from 'Phaser/GameObjects/OSMDIterator';
import ITimeKeeper from './ITimeKeeper';
import { isErrorState, isSemiErrorState } from 'Utils';
import { sum, range, every } from 'lodash';
import { v4 as uuid } from 'uuid';
import Phrase, { Iterator, IteratorPhaser } from './Phrase';
import { COLORBLIND_TYPE, ERROR_REC_CLASS, HANDEDNESS } from 'Utils/Constants';
import logger from 'Utils/Logger';
import Logger from 'Utils/Logger';
import {
  getEventInputLatencyMs,
  getInputLatencyMeasureOffset,
  getLatencyAdjustedMeasureTimestamp,
} from 'Utils/AudioInputLatency';
import { emitLocalAudioGradeEvent } from 'Utils/LocalAudioSessionEvents';

const onsetDefault = 0.3;
const offsetDefault = 0.3;

// BPM-adaptive early onset window bounds (in seconds).
// At higher BPMs the fixed OSMD-unit window shrinks in real time; the floor
// prevents it from becoming too tight. The ceiling prevents overly generous
// windows at very low BPMs.
export const MIN_EARLY_ONSET_SEC = 0.225; // 225ms floor — matches the 60 BPM window at all tempos
export const MAX_EARLY_ONSET_SEC = 0.25; // 250ms ceiling

// Minimum real-time gap (seconds) between two note-on events of the SAME pitch
// AND velocity for the second to be treated as a genuine re-articulation rather
// than a hardware/driver double-trigger ("key chatter"). A human cannot
// re-press the same key within 10ms — typical chatter is sub-5ms — so a second
// identical note-on inside this window is dropped. Without this guard the
// duplicate is matched as an extra/"additional" note against the already-
// satisfied target and repaints a cleanly-hit note yellow.
export const DUPLICATE_NOTE_ON_MIN_GAP_SEC = 0.01; // 10ms
const AUDIO_RELEASE_EVENT_TIMEOUT_MS = 900;

export type EventInputTiming = {
  source?: 'midi' | 'audio-recognition';
  observedAtMs?: number;
};

/**
 * Compute a BPM-adjusted correctStartTimestamp for early onset detection.
 * Converts the static OSMD-unit window to real-time seconds, clamps it
 * between MIN/MAX bounds, and converts back to OSMD units.
 */
export function bpmAdjustedEarlyOnsetBoundary(
  onsetTimestamp: number,
  staticCorrectStart: number,
  bpm: number,
): number {
  const staticWindowOSMD = onsetTimestamp - staticCorrectStart;
  const staticWindowSec = (staticWindowOSMD * 240) / bpm;
  const clampedWindowSec = Math.min(
    Math.max(staticWindowSec, MIN_EARLY_ONSET_SEC),
    MAX_EARLY_ONSET_SEC,
  );
  const clampedWindowOSMD = (clampedWindowSec * bpm) / 240;
  return onsetTimestamp - clampedWindowOSMD;
}

// All timing configuration settings are percentages and relative to the length of the note/rest being played.
// For instance, a quarter note with a correct start onset allowance of 0.15 will be considered in the green zone
// as long as the MIDI onset event is received within 15% of the quarter note's duration at the quarter note's expected onset.
export class TimingOffsetsConfig {
  // Left margin percentage around a note's onset for the correct or green zone.
  correctNoteOnsetStartPct = onsetDefault * 0.75;
  // Right margin percentage around a note's onset for the correct or green zone.
  correctNoteOnsetEndPct = 0.2;
  // Left margin percentage around a note's onset for the mistimed or yellow zone.
  mistimedNoteOnsetStartPct = 0.35;
  // Right margin percentage around a note's onset for the mistimed or yellow zone.
  mistimedNoteOnsetEndPct = 0.6;

  // Left margin percentage around a note's offset for the correct or green zone.
  correctNoteOffsetStartPct = offsetDefault;
  // Right margin percentage around a note's offset for the correct or green zone.
  correctNoteOffsetEndPct = offsetDefault;
  // Left margin percentage around a note's offset for the mistimed or yellow zone.
  mistimedNoteOffsetStartPct = 0.35;
  // Right margin percentage around a note's offset for the mistimed or yellow zone.
  mistimedNoteOffsetEndPct = 0.25;

  // The margin percentaes of rests SHRINK their bucket rather than growing it.

  // Left margin correct percentage around the start of the rest.
  // The correct zone shrinks "inward" (past the rest's onset) by this percentage.
  correctRestOnsetPct = 0.1;
  // Left margin mistimed percentage around the start of the rest.
  // The mistimed zone shrinks "inward" (past the rest's onset) by this percentage.
  mistimedRestOnsetPct = 0.2;
  // Right margin correcct percentage around the end of the rest.
  // The correct zone shrinks "inward" (before the rest's offset) by this percentage.
  correctRestOffsetPct = 0.25;
  // Right margin mistimed percentage around the end of the rest.
  // The mistimed zone shrinks "inward" (before the rest's offset) by this percentage.
  mistimedRestOffsetPct = 0.25;
}

// Timing offsets are relative to the expected timing for note on/off events.
export class TimingOffsets {
  // The max allowable timestamp ahead of the expected event time
  // for an event to be considered correctly timed in the correct or in the "green zone".
  correctStartTimestamp: number;
  // The max allowable timestamp after the expected event time
  // for an event to be considered correctly timed in the correct or in the "green zone".
  correctEndTimestamp: number;
  // The max allowable timestamp ahead of the expected event time
  // for an event to be considered mistimed or in the "yellow zone".
  mistimedStartTimestamp: number;
  // The max allowable timestamp after the expected event time
  // for an event to be considered mistimed or in the "yellow zone".
  mistimedEndTimestamp: number;

  constructor(
    correctStartTimestamp: number,
    correctEndTimestamp: number,
    mistimedStartTimestamp: number,
    mistimedEndTimestamp: number,
  ) {
    this.correctStartTimestamp = correctStartTimestamp;
    this.correctEndTimestamp = correctEndTimestamp;
    this.mistimedStartTimestamp = mistimedStartTimestamp;
    this.mistimedEndTimestamp = mistimedEndTimestamp;
  }
}

// Base class encapsulating common properties and methods for note and rest events.
export class BaseNoteEvent {
  protected length: number;
  protected timestamp: number;
  protected offsetTimestamp: number | null = null;
  private playedTimestamp: number | null = null;
  protected _hand: HANDEDNESS;
  protected snapshotPhraseAccuracy?: number = undefined;
  protected _LhSnapshotPhraseAccuracy?: number = undefined;
  protected _RhSnapshotPhraseAccuracy?: number = undefined;
  protected snapshotBPM?: number = undefined;
  // MUS-469: gameplay mode snapshotted onto the note at evaluation time
  // (true = Free Play). Undefined until the stream stamps it; the emit layer
  // sends it through as-is so an unstamped note lands as null, not false.
  protected freePlay?: boolean = undefined;
  protected errorScore: number = 0;
  protected eventBucket: EventBucket;
  private onsetState: MidiEventState = MidiEventState.UNSET;
  private offsetState: MidiEventState = MidiEventState.UNSET;

  constructor(eventBucket: EventBucket, timestamp: number, length: number, hand: HANDEDNESS) {
    this.eventBucket = eventBucket;
    this.timestamp = timestamp;
    this.length = length;
    this._hand = hand;
  }

  public set OnsetState(eventState: MidiEventState) {
    this.onsetState = eventState;
  }

  public set OffsetState(eventState: MidiEventState) {
    this.offsetState = eventState;
  }
  public get Hand() {
    return this._hand;
  }

  public set SnapshotBPM(bpm: number) {
    this.snapshotBPM = bpm;
  }

  public set SnapshotPhraseAccuracy(accuracy: number) {
    this.snapshotPhraseAccuracy = accuracy;
  }

  public set RhSnapshotPhraseAccuracy(accuracy: number) {
    this._RhSnapshotPhraseAccuracy = accuracy;
  }

  public set LhSnapshotPhraseAccuracy(accuracy: number) {
    this._LhSnapshotPhraseAccuracy = accuracy;
  }

  public set PlayedTimestamp(playedTimestamp: number | null) {
    this.playedTimestamp = playedTimestamp;
  }

  public get OnsetState(): MidiEventState {
    return this.onsetState;
  }

  public get OffsetState(): MidiEventState {
    return this.offsetState;
  }

  public get SnapshotPhraseAccuracy() {
    return this.snapshotPhraseAccuracy || 0;
  }

  public get LhSnapshotPhraseAccuracy() {
    return this._LhSnapshotPhraseAccuracy || 0;
  }

  public get RhSnapshotPhraseAccuracy() {
    return this._RhSnapshotPhraseAccuracy || 0;
  }

  public get SnapshotBPM() {
    return this.snapshotBPM || 0;
  }

  // Null-preserving reads for the telemetry emit boundary (MUS-468 / MUS-470).
  // The coercing getters above turn an unstamped field into 0, which other
  // callers (the on-screen running-accuracy display, calc*) rely on - but at
  // the emit boundary that 0 is indistinguishable from a genuine 0. These use
  // ?? so a *stamped* 0 is preserved while a field never snapshotted on a given
  // grading path (e.g. LH/RH on a WRONG_NOTE, accuracy on a NEVER_PLAYED note)
  // lands as null rather than a fabricated 0. Do not fold these into the getters
  // above; the || 0 contract is load-bearing for the other callers.
  public get SnapshotPhraseAccuracyRaw(): number | null {
    return this.snapshotPhraseAccuracy ?? null;
  }

  public get LhSnapshotPhraseAccuracyRaw(): number | null {
    return this._LhSnapshotPhraseAccuracy ?? null;
  }

  public get RhSnapshotPhraseAccuracyRaw(): number | null {
    return this._RhSnapshotPhraseAccuracy ?? null;
  }

  public get SnapshotBPMRaw(): number | null {
    return this.snapshotBPM ?? null;
  }

  public set FreePlay(freePlay: boolean | undefined) {
    this.freePlay = freePlay;
  }

  // Intentionally NOT coerced (unlike SnapshotBPM above): return the raw
  // boolean|undefined so the emit layer can distinguish "never stamped" from
  // "stamped false" and send null in the former case (MUS-469).
  public get FreePlay() {
    return this.freePlay;
  }

  public get Length() {
    return this.length;
  }

  public set Length(length: number) {
    this.length = length;
  }

  public get Timestamp() {
    return this.timestamp;
  }

  public set Timestamp(timestamp: number) {
    this.timestamp = timestamp;
  }

  public get OffsetTimestamp() {
    return this.offsetTimestamp;
  }

  public set OffsetTimestamp(offsetTimestamp: number | null) {
    this.offsetTimestamp = offsetTimestamp;
  }

  public set ErrorScore(errorScore: number) {
    this.errorScore = errorScore;
  }

  public get EventBucket() {
    return this.eventBucket;
  }

  public get PlayedTimestamp() {
    return this.playedTimestamp;
  }

  public get ErrorScore() {
    return this.errorScore;
  }
}

// Represents a single note event.
export class NoteEvent extends BaseNoteEvent {
  private midiNote: number;
  private noteOnTimingOffsets: TimingOffsets;
  private noteOffTimingOffsets: TimingOffsets;
  private graphics: PrerenderedGraphics[];
  private phraseRef?: Phrase;
  private additionalNoteRef: AdditionalNoteEvent | null = null;
  // Notehead highlighting: for a folded tie-start with >1 head, the bucket-space
  // timestamp of each head, index-aligned to `graphics`. undefined for untied
  // notes (and single-head ties) - those keep whole-array colouring. Used by the
  // green-subset paint so heads green progressively as the cursor reaches them;
  // yellow/red still colour the whole `graphics` array (all heads at once).
  private headTimestamps?: number[];

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    midiNote: number,
    noteOnTimingOffsets: TimingOffsets,
    noteOffTimingOffsets: TimingOffsets,
    graphics: PrerenderedGraphics[],
    length: number,
    phraseRef: Phrase | undefined,
    hand: HANDEDNESS,
  ) {
    super(eventBucket, timestamp, length, hand);
    this.midiNote = midiNote;
    this.noteOnTimingOffsets = noteOnTimingOffsets;
    this.noteOffTimingOffsets = noteOffTimingOffsets;
    this.graphics = graphics;
    this.phraseRef = phraseRef;
  }

  public get MidiNote() {
    return this.midiNote;
  }

  public set MidiNote(midiNote: number) {
    this.midiNote = midiNote;
  }

  public get NoteOnTimingOffsets() {
    return this.noteOnTimingOffsets;
  }

  public set NoteOnTimingOffsets(noteOnTimingOffsets: TimingOffsets) {
    this.noteOnTimingOffsets = noteOnTimingOffsets;
  }

  public get NoteOffTimingOffsets() {
    return this.noteOffTimingOffsets;
  }

  public set NoteOffTimingOffsets(noteOffTimingOffsets: TimingOffsets) {
    this.noteOffTimingOffsets = noteOffTimingOffsets;
  }

  public get Graphics() {
    return this.graphics;
  }

  public set Graphics(graphics: PrerenderedGraphics[]) {
    this.graphics = graphics;
  }

  public get HeadTimestamps() {
    return this.headTimestamps;
  }

  public set HeadTimestamps(headTimestamps: number[] | undefined) {
    this.headTimestamps = headTimestamps;
  }

  public get PhraseRef() {
    return this.phraseRef;
  }

  public set PhraseRef(phraseRef: Phrase | undefined) {
    this.phraseRef = phraseRef;
  }

  public get AdditionalNote() {
    return this.additionalNoteRef;
  }

  public set AdditionalNote(note: AdditionalNoteEvent | null) {
    this.additionalNoteRef = note;
  }
}

// Represents a rest event.
export class RestEvent extends BaseNoteEvent {
  private timingOffsets: TimingOffsets;
  private state: MidiEventState = MidiEventState.UNSET;
  private graphics: PrerenderedGraphics[];
  private hidden: boolean;
  private additionalNoteRef: AdditionalNoteEvent | null = null;

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    timingOffsets: TimingOffsets,
    graphics: PrerenderedGraphics[],
    length: number,
    hidden: boolean,
    hand: HANDEDNESS,
  ) {
    super(eventBucket, timestamp, length, hand);
    this.timingOffsets = timingOffsets;
    this.graphics = graphics;
    this.hidden = hidden;
  }

  public set AdditionalNote(note: AdditionalNoteEvent | null) {
    this.additionalNoteRef = note;
  }

  public get TimingOffsets() {
    return this.timingOffsets;
  }

  public get State() {
    return this.state;
  }

  public set State(state: MidiEventState) {
    this.state = state;
  }

  public get Graphics() {
    return this.graphics;
  }

  public get Hidden() {
    return this.hidden;
  }

  public get AdditionalNote(): AdditionalNoteEvent | null {
    return this.additionalNoteRef;
  }
}

// Represents an additional note event.
export class AdditionalNoteEvent extends BaseNoteEvent {
  private _playedNote: number;

  constructor(
    eventBucket: EventBucket,
    timestamp: number,
    playedTimestamp: number,
    playedNote: number,
    hand: HANDEDNESS,
  ) {
    super(eventBucket, timestamp, 0, hand); // EventBucket is not applicable for AdditionalNoteEvent
    this._playedNote = playedNote;
    this.PlayedTimestamp = playedTimestamp;
  }

  public setOffsetTimestamp(offsetTimestamp: number) {
    this.offsetTimestamp = offsetTimestamp;
  }

  public get PlayedNote() {
    return this._playedNote;
  }
}

export class EventBucket {
  // Any audible notes that are played at this timestamp.
  notes: NoteEvent[];

  // We process rests differently from notes;
  // they get their own list to keep things cleaner.
  rests: RestEvent[];

  // The exact number of timestamp that events in this bucket onset at.
  // Per-note timing offsets are added and subtracted from this number.
  timestamp: number;

  // The earliest timestamp that a MIDI event could match on a note/rest in this bucket.
  // Before this time has passed, there's no reason to look inside this bucket when matching MIDI note ON events.
  minOnsetTimestamp: number = -1;

  // The latest timestamp that a MIDI event could match on a note/rest in this bucket.
  // After this time has passed, there's no reason to look inside this bucket when matching MIDI note ON events.
  maxOnsetTimestamp: number = -1;

  // After this timestamp has passed and we aren't waiting for any more offset events,
  // the playback data from this bucket can be uploaded to the backend.
  maxOffsetTimestamp: number = -1;

  // PhraseRef provides a link back to the phrase associated with the bucket
  phraseRef?: Phrase;

  additinalNotes: AdditionalNoteEvent[] = [];

  constructor(
    timestamp: number,
    notes: NoteEvent[],
    rests: RestEvent[],
    phraseRef: Phrase | undefined,
  ) {
    this.timestamp = timestamp;
    this.notes = notes;
    this.rests = rests;
    this.phraseRef = phraseRef;

    this.minOnsetTimestamp = 0;
    this.maxOnsetTimestamp = 0;
    this.maxOffsetTimestamp = 0;
    for (const note of notes) {
      if (note.NoteOnTimingOffsets.mistimedStartTimestamp < this.minOnsetTimestamp) {
        this.minOnsetTimestamp = note.NoteOnTimingOffsets.mistimedStartTimestamp;
      }
      // will probably need to revisit this
      if (note.NoteOnTimingOffsets.mistimedEndTimestamp > this.maxOnsetTimestamp) {
        this.maxOnsetTimestamp = note.NoteOnTimingOffsets.mistimedEndTimestamp;
      }
      if (note.NoteOnTimingOffsets.mistimedEndTimestamp > this.maxOffsetTimestamp) {
        this.maxOffsetTimestamp = note.NoteOnTimingOffsets.mistimedEndTimestamp;
      }
    }
  }

  addEvents(notes: NoteEvent[], rests: RestEvent[]) {
    this.notes.push(...notes);
    this.rests.push(...rests);
  }

  pushAdditionalNote(additionalNotes: AdditionalNoteEvent) {
    this.additinalNotes.push(additionalNotes);
  }

  sort() {
    this.notes.sort(
      (n1, n2) =>
        n1.NoteOnTimingOffsets.mistimedStartTimestamp -
        n2.NoteOnTimingOffsets.mistimedStartTimestamp,
    );
    this.rests.sort(
      (r1, r2) => r1.TimingOffsets.correctEndTimestamp - r2.TimingOffsets.correctEndTimestamp,
    );

    // Defense-in-depth invariant: an empty bucket must never yield +/-Infinity
    // bounds (Math.min/Math.max of nothing), which would corrupt ordering.
    // addEvents2 no longer registers empty buckets, but this guards every other
    // caller of sort() and any future empty-bucket path.
    if (this.notes.length === 0 && this.rests.length === 0) {
      this.minOnsetTimestamp = this.timestamp;
      this.maxOnsetTimestamp = this.timestamp;
      this.maxOffsetTimestamp = this.timestamp;
    } else {
      this.minOnsetTimestamp = Math.min(
        ...this.notes.map((n) => n.NoteOnTimingOffsets.mistimedStartTimestamp),
        ...this.rests.map((r) => r.TimingOffsets.correctStartTimestamp),
      );
      this.maxOnsetTimestamp = Math.max(
        ...this.notes.map((n) => n.NoteOnTimingOffsets.mistimedEndTimestamp),
        ...this.rests.map((r) => r.TimingOffsets.mistimedEndTimestamp),
      );
      this.maxOffsetTimestamp = Math.max(
        ...this.notes.map((n) => n.NoteOffTimingOffsets.mistimedEndTimestamp),
        ...this.rests.map((r) => r.TimingOffsets.correctEndTimestamp),
      );
    }
  }
}

export type PlaybackNoteEvent = {
  id: string;
  midiNote: number;
  timestamp: number;
  offsetTimestamp: number;
};

export type ExpectedMidiSnapshot = {
  midiNotes: number[];
  expectationKey: string;
  events: ExpectedMidiEvent[];
};

export type ExpectedMidiEvent = {
  id: string;
  midiNote: number;
  onsetTimestamp: number;
  offsetTimestamp: number;
  hand: HANDEDNESS;
  phase: 'attack' | 'sustain' | 'release';
};

export default class EventStream {
  timeKeeper: ITimeKeeper;
  private offsetsConfig: TimingOffsetsConfig;
  private eventBuckets: EventBucket[] = [];
  private phraseHistory: Phrase[] = [];
  private activeOnsetEvents: (NoteEvent | AdditionalNoteEvent | null)[];
  private activeOnsetInputSources: Array<EventInputTiming['source'] | null> = [];
  private pendingInputSourceByMidi = new Map<number, EventInputTiming['source'] | null>();
  private lastProcessedTimestamp: number = -1;
  private onsetIndex: number = 0;
  private missedNotesIndex: number = 0;
  private _lookbackWindowSize = 40;
  private _weightedAccuracyDivisions: number;
  private _weightedAccuracyHitVelocity: number;
  private _weightedAccuracyMissVelocity: number;
  private _realNotesMarkedCount: number = 0;

  private correctNoteMagnitude: number = 1;
  private offNoteMagnitude: number = 0.8;
  private missMagnitude: number = 0.3;
  accuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[] = [];
  lhAccuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[] = [];
  rhAccuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[] = [];
  id = uuid();
  private useRepertoireMarkAccuracy: boolean;
  private loopStartTimestamp: number | null = null;
  private loopEndTimestamp: number | null = null;
  // PPC hook: invoked the instant a note inside the active loop range is graded
  // yellow (semi-error) / red (error), so the perfect-phrase counter can mark
  // the current iteration not-perfect immediately — without waiting for the
  // ~60 Hz poll and overriding any momentary 100% latch. Set by the scene.
  private onLoopPerfectionBroken?: () => void;
  // Effective hand-accuracy state actually consulted when matching/coloring notes.
  public errorRecognitionActive: ErrorRecognitionActive = { left: true, right: true };
  // The user's desired hand-accuracy setting (driven from Redux). Kept separate
  // from `errorRecognitionActive` so that a loop count-in can temporarily
  // SUPPRESS recognition (force both hands off while the cursor is parked at the
  // bracket start, so nothing is marked) without discarding the user's intent —
  // and so a toggle made DURING that count-in still takes effect on restore.
  public userErrorRecognitionActive: ErrorRecognitionActive = { left: true, right: true };
  // True while recognition is force-disabled for a loop count-in window.
  public errorRecognitionSuppressed = false;
  // MUS-469: current gameplay mode (true = Free Play, chevrons off). Kept in
  // sync by the scene via SetFreePlayMode (same wiring as SnapshotBPM's tempo),
  // and snapshotted onto each note at evaluation so the continuously-toggleable
  // mode is captured at note grain. Telemetry only - does not affect gameplay.
  public freePlayMode: boolean = false;
  // while this seems more UI related than midi-event processing related, the eventstream changes the color
  // of notes based on correctness, so must have access to that information, but doesn't have access to redux.
  private colorblindType: COLORBLIND_TYPE | null;
  private inputLatencyMs: number = 0;
  // Tracks the musical timestamp of the last ACCEPTED note-on per
  // `${midiNote}:${velocity}`, used to reject hardware/driver double-triggers
  // (see DUPLICATE_NOTE_ON_MIN_GAP_SEC and isDuplicateNoteOn). Cleared on reset().
  private lastNoteOnTimestampByPitchVelocity = new Map<string, number>();

  constructor(
    timeKeeper: ITimeKeeper,
    offsetsConfig: TimingOffsetsConfig,
    colorblindType: COLORBLIND_TYPE | null,
    weightedAccuracyDivisions: number = 1,
    weightedAccuracyHitVelocity: number = 0.025,
    weightedAccuracyMissVelocity: number = 0.1,
    useRepertoireMarkAccuracy: boolean = false,
  ) {
    this.timeKeeper = timeKeeper;
    this.offsetsConfig = offsetsConfig;
    this._weightedAccuracyDivisions = weightedAccuracyDivisions;
    this._weightedAccuracyHitVelocity = weightedAccuracyHitVelocity;
    this._weightedAccuracyMissVelocity = weightedAccuracyMissVelocity;
    this.useRepertoireMarkAccuracy = useRepertoireMarkAccuracy;
    this.colorblindType = colorblindType;
    if (this.useRepertoireMarkAccuracy) {
      this._lookbackWindowSize = 0;
    }

    this.activeOnsetEvents = [];
    this.setupOnsetEvents();
    this.initAccuracyQueues();
  }

  get PhraseHistory() {
    return this.phraseHistory;
  }

  // workaround because the midi context loses reference to state. So, turn off error recognition here.
  // Records the user's intent. While suppressed (loop count-in) the effective
  // state stays disabled, but the new intent is remembered so it applies as
  // soon as recognition is un-suppressed — otherwise a hand toggle made during
  // a loop count-in would be clobbered by the pre-count-in snapshot on restore.
  setErrorRecognitionActive(errorRecognitionActive: ErrorRecognitionActive) {
    this.userErrorRecognitionActive = errorRecognitionActive;
    if (!this.errorRecognitionSuppressed) {
      this.errorRecognitionActive = errorRecognitionActive;
    }
  }

  // Temporarily force error recognition off for a loop count-in without losing
  // the user's hand-accuracy intent. Idempotent.
  suppressErrorRecognition() {
    this.errorRecognitionSuppressed = true;
    this.errorRecognitionActive = { left: false, right: false };
  }

  // End a suppression window and re-apply the user's current intent (which may
  // have changed via a toggle made mid-count-in). Safe to call when not
  // suppressed — it just re-syncs the effective state to the intent.
  unsuppressErrorRecognition() {
    this.errorRecognitionSuppressed = false;
    this.errorRecognitionActive = { ...this.userErrorRecognitionActive };
  }

  setInputLatencyMs(inputLatencyMs: number) {
    if (!Number.isFinite(inputLatencyMs)) return;
    this.inputLatencyMs = Math.max(0, inputLatencyMs);
  }

  private getLatencyAdjustedTimestamp(inputTiming?: EventInputTiming) {
    const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
    if (inputTiming?.source && inputTiming.source !== 'audio-recognition') {
      return timestamp;
    }

    const effectiveInputLatencyMs = inputTiming
      ? getEventInputLatencyMs(this.inputLatencyMs, inputTiming)
      : this.inputLatencyMs;
    return getLatencyAdjustedMeasureTimestamp(timestamp, this.timeKeeper, effectiveInputLatencyMs);
  }

  setLoopBounds(startTimestamp: number, endTimestamp: number): void {
    console.log(
      `[LOOP-DBG] EventStream.setLoopBounds start=${startTimestamp} end=${endTimestamp} id=${this.id}`,
    );
    this.loopStartTimestamp = startTimestamp;
    this.loopEndTimestamp = endTimestamp;
  }

  clearLoopBounds(): void {
    console.log(
      `[LOOP-DBG] EventStream.clearLoopBounds (was start=${this.loopStartTimestamp} end=${this.loopEndTimestamp}) id=${this.id}`,
    );
    this.loopStartTimestamp = null;
    this.loopEndTimestamp = null;
  }

  /**
   * Register the PPC hook fired when a note inside the active loop range is
   * graded yellow/red (see `onLoopPerfectionBroken`). The scene uses it to drop
   * the perfect-phrase streak the instant a semi-error/error appears.
   */
  setLoopPerfectionBrokenCallback(callback: (() => void) | undefined): void {
    this.onLoopPerfectionBroken = callback;
  }

  /**
   * PPC hook: when a note grading inside the active loop range paints it yellow
   * (semi-error) or red (error), notify the perfect-phrase counter immediately
   * so the current iteration is marked not-perfect — even if it had momentarily
   * latched at a clean 100% (e.g. an early release of the loop's final note
   * grading EARLY_OFFSET after every onset was already green). A valid
   * anticipatory early start of the NEXT iteration is exempt: it belongs to that
   * iteration's count-in, not this loop (see loopRangeAccuracy /
   * isAnticipatoryEarlyStart).
   */
  private notifyIfLoopPerfectionBroken(
    note: BaseNoteEvent,
    state: MidiEventState,
    extra: AdditionalNoteEvent | null,
  ): void {
    if (!this.onLoopPerfectionBroken) return;
    if (this.loopStartTimestamp === null || this.loopEndTimestamp === null) return;
    // Only gradings inside the active loop range are relevant.
    if (note.Timestamp < this.loopStartTimestamp || note.Timestamp >= this.loopEndTimestamp) return;
    // Ignore hands whose accuracy is off — they're never graded or scored.
    if (!this.checkAccuracyNoteHand(note)) return;
    // Only yellow (semi-error) / red (error) states break perfection.
    if (!isErrorState(state) && !isSemiErrorState(state)) return;
    // Exempt a valid early start of the next iteration (a forgiven extra note).
    if (state === MidiEventState.ADDITIONAL_NOTE && extra) {
      const allowance = this.computeLoopEarlyStartAllowance(
        this.loopStartTimestamp,
        this.loopEndTimestamp,
      );
      if (this.isAnticipatoryEarlyStart(extra, allowance)) return;
    }
    console.log(
      `[PPC] EventStream broke loop perfection: noteTs=${note.Timestamp} state=${state} (loop [${this.loopStartTimestamp},${this.loopEndTimestamp}))`,
    );
    this.onLoopPerfectionBroken();
  }

  initAccuracyQueues = () => {
    this.accuracyQueue = range(this._lookbackWindowSize).map(
      () => ({ ErrorScore: 0 }) as NoteEvent,
    );
    this.lhAccuracyQueue = range(this._lookbackWindowSize).map(
      () => ({ ErrorScore: 0 }) as NoteEvent,
    );
    this.rhAccuracyQueue = range(this._lookbackWindowSize).map(
      () => ({ ErrorScore: 0 }) as NoteEvent,
    );
  };

  accuracyQueueLength = () => this.accuracyQueue.length;

  totalNotesLength = () => {
    let totalNotesSum = 0;
    this.eventBuckets.forEach((el) => (totalNotesSum += el.notes.length));
    return totalNotesSum;
  };

  private addExpectedMidiNotesFromBucket(
    bucket: EventBucket,
    bucketIndex: number,
    timestamp: number,
    expectedMidiNotes: Set<number>,
    expectedEvents: Map<string, ExpectedMidiEvent>,
  ): void {
    bucket.notes.forEach((note, noteIndex) => {
      if (!this.checkAccuracyNoteHand(note)) return;
      if (note.OffsetState !== MidiEventState.UNSET) return;
      expectedMidiNotes.add(note.MidiNote);
      const expectedEvent = this.toExpectedMidiEvent(
        note,
        bucketIndex,
        noteIndex,
        this.getExpectedMidiEventPhase(note, timestamp),
      );
      expectedEvents.set(expectedEvent.id, expectedEvent);
    });
  }

  private getExpectedMidiEventPhase(
    note: NoteEvent,
    timestamp: number,
  ): ExpectedMidiEvent['phase'] {
    if (note.OnsetState === MidiEventState.UNSET) return 'attack';
    if (timestamp >= note.NoteOffTimingOffsets.correctStartTimestamp) return 'release';
    return 'sustain';
  }

  private toExpectedMidiEvent(
    note: NoteEvent,
    bucketIndex: number,
    noteIndex: number,
    phase: ExpectedMidiEvent['phase'],
  ): ExpectedMidiEvent {
    const phraseId = note.PhraseRef?.uuid ?? 'phrase';
    return {
      id: `${phraseId}:${bucketIndex}:${noteIndex}:${note.Timestamp}:${note.MidiNote}`,
      midiNote: note.MidiNote,
      onsetTimestamp: note.Timestamp,
      offsetTimestamp: note.Timestamp + note.Length,
      hand: note.Hand,
      phase,
    };
  }

  getExpectedMidiSnapshot(): ExpectedMidiSnapshot {
    const expectedMidiNotes = new Set<number>();
    const expectedEvents = new Map<string, ExpectedMidiEvent>();
    const timestamp = this.getLatencyAdjustedTimestamp();

    for (const activeEvent of this.activeOnsetEvents) {
      if (!(activeEvent instanceof NoteEvent)) continue;
      if (!this.checkAccuracyNoteHand(activeEvent)) continue;
      if (activeEvent.OffsetState !== MidiEventState.UNSET) continue;
      expectedMidiNotes.add(activeEvent.MidiNote);
      const bucketIndex = this.eventBuckets.indexOf(activeEvent.EventBucket);
      const noteIndex = activeEvent.EventBucket.notes.indexOf(activeEvent);
      const expectedEvent = this.toExpectedMidiEvent(
        activeEvent,
        bucketIndex,
        noteIndex,
        this.getExpectedMidiEventPhase(activeEvent, timestamp),
      );
      expectedEvents.set(expectedEvent.id, expectedEvent);
    }

    if (this.eventBuckets.length === 0) {
      const midiNotes = Array.from(expectedMidiNotes).sort((left, right) => left - right);
      const events = Array.from(expectedEvents.values());
      return {
        midiNotes,
        expectationKey:
          events
            .map((event) => event.id)
            .sort()
            .join('|') || `midi:${midiNotes.join(',')}`,
        events,
      };
    }

    let foundCurrentWindow = false;

    for (let i = Math.max(0, this.onsetIndex - 1); i < this.eventBuckets.length; i++) {
      const bucket = this.eventBuckets[i];

      if (timestamp < bucket.minOnsetTimestamp) {
        if (!foundCurrentWindow && expectedMidiNotes.size === 0) {
          this.addExpectedMidiNotesFromBucket(
            bucket,
            i,
            timestamp,
            expectedMidiNotes,
            expectedEvents,
          );
        }
        break;
      }

      if (timestamp > bucket.maxOffsetTimestamp) {
        continue;
      }

      foundCurrentWindow = true;
      this.addExpectedMidiNotesFromBucket(bucket, i, timestamp, expectedMidiNotes, expectedEvents);
    }

    if (expectedMidiNotes.size === 0) {
      const fallbackBucket = this.eventBuckets.find((bucket, index) => {
        if (index < this.onsetIndex) return false;
        return bucket.notes.some(
          (note) => this.checkAccuracyNoteHand(note) && note.OffsetState === MidiEventState.UNSET,
        );
      });

      if (fallbackBucket) {
        this.addExpectedMidiNotesFromBucket(
          fallbackBucket,
          this.eventBuckets.indexOf(fallbackBucket),
          timestamp,
          expectedMidiNotes,
          expectedEvents,
        );
      }
    }

    const midiNotes = Array.from(expectedMidiNotes).sort((left, right) => left - right);
    const events = Array.from(expectedEvents.values()).sort(
      (left, right) =>
        left.onsetTimestamp - right.onsetTimestamp ||
        left.midiNote - right.midiNote ||
        left.id.localeCompare(right.id),
    );
    return {
      midiNotes,
      expectationKey:
        events
          .map((event) => event.id)
          .sort()
          .join('|') || `midi:${midiNotes.join(',')}`,
      events,
    };
  }

  getExpectedMidiNotes(): number[] {
    return this.getExpectedMidiSnapshot().midiNotes;
  }

  private toPlaybackNoteEvent(
    bucketIndex: number,
    noteIndex: number,
    note: NoteEvent,
  ): PlaybackNoteEvent {
    return {
      id: `${bucketIndex}:${noteIndex}:${note.Timestamp}:${note.MidiNote}`,
      midiNote: note.MidiNote,
      timestamp: note.Timestamp,
      offsetTimestamp: note.Timestamp + note.Length,
    };
  }

  getPlaybackNoteEventsBetween(startTimestamp: number, endTimestamp: number): PlaybackNoteEvent[] {
    if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp)) return [];
    if (endTimestamp < startTimestamp) return [];

    const playbackEvents: PlaybackNoteEvent[] = [];
    this.eventBuckets.forEach((bucket, bucketIndex) => {
      if (bucket.timestamp <= startTimestamp || bucket.timestamp > endTimestamp) return;

      bucket.notes.forEach((note, noteIndex) => {
        playbackEvents.push(this.toPlaybackNoteEvent(bucketIndex, noteIndex, note));
      });
    });

    return playbackEvents.sort(
      (left, right) => left.timestamp - right.timestamp || left.midiNote - right.midiNote,
    );
  }

  getActivePlaybackNoteEventsAtTimestamp(timestamp: number): PlaybackNoteEvent[] {
    if (!Number.isFinite(timestamp)) return [];

    const playbackEvents: PlaybackNoteEvent[] = [];
    this.eventBuckets.forEach((bucket, bucketIndex) => {
      bucket.notes.forEach((note, noteIndex) => {
        const offsetTimestamp = note.Timestamp + note.Length;
        if (note.Timestamp <= timestamp && timestamp < offsetTimestamp) {
          playbackEvents.push(this.toPlaybackNoteEvent(bucketIndex, noteIndex, note));
        }
      });
    });

    return playbackEvents.sort(
      (left, right) => left.timestamp - right.timestamp || left.midiNote - right.midiNote,
    );
  }

  rollBackWindowFull = () => this.accuracyQueue.length >= this._lookbackWindowSize;

  // Returns the number of actual notes that have been marked for accuracy (not dummy initialized notes)
  hasRealNotesMarked = () => this._realNotesMarkedCount > 0;

  // Mark state. If current state is anything but unset, that value is removed from the queue and replaced.
  private markAccuracy(
    accuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[],
    noteEvent: NoteEvent | RestEvent | AdditionalNoteEvent,
  ) {
    if (noteEvent.OnsetState === MidiEventState.UNSET) {
      accuracyQueue.push(noteEvent);
      if (!this.useRepertoireMarkAccuracy) {
        // Repertoire does not use lookback window and doesn't need to cut array
        if (accuracyQueue.length > this._lookbackWindowSize) {
          accuracyQueue.shift();
        }
      }
    }
  }

  private serErrorScore(note: NoteEvent | RestEvent, eventState: MidiEventState) {
    if (
      eventState === MidiEventState.ADDITIONAL_NOTE &&
      note.AdditionalNote !== null &&
      note.ErrorScore == 0
    ) {
      note.ErrorScore = 0.7;
    } else if (
      isSemiErrorState(eventState) &&
      eventState !== MidiEventState.ADDITIONAL_NOTE &&
      note.ErrorScore !== 1
    ) {
      note.ErrorScore = 0.7;
    } else if (isErrorState(eventState)) {
      note.ErrorScore = 1;
    }
  }

  private markAccuracyBothHands(note: NoteEvent | RestEvent, eventState: MidiEventState) {
    // in the case of a hit or unset, if there is an additional note, we want to adjust the score to
    // adjust for the error
    this.serErrorScore(note, eventState);
    this.markAccuracy(this.accuracyQueue, note);
    // Track that a real note has been marked for accuracy (not a dummy initialized note)
    this._realNotesMarkedCount++;
    if (note.Hand === 'lh') {
      this.markAccuracy(this.lhAccuracyQueue, note);
    } else if (note.Hand === 'rh') {
      this.markAccuracy(this.rhAccuracyQueue, note);
    }
  }

  /**
   * Green the heads of a folded tie that the cursor has reached, leaving
   * not-yet-reached heads their current colour. Stateless: the reached set is
   * recomputed from `cursorTimestamp` on every call, so it is idempotent (already-
   * green heads re-green to a no-op) and loop/rewind-safe (a re-entry re-greens
   * from the first head). `graphics` and `HeadTimestamps` are index-aligned by
   * construction; a head with an undefined timestamp, or one the cursor has not
   * yet reached, is skipped. A head whose graphic never rendered (off-page GNote)
   * is simply absent from `graphics`, so it drops out silently rather than throwing.
   */
  private paintTieGreenSubset(note: NoteEvent, cursorTimestamp: number) {
    const reached = reachedTieHeads(note.Graphics, note.HeadTimestamps, cursorTimestamp);
    if (reached.length > 0) {
      this.makeNoteCorrect(reached, this.colorblindType, note.Timestamp);
    }
  }

  /**
   * Ascending-pitch rank (0 = lowest) and size of the same-hand chord this note
   * belongs to - the notes sharing this note's StaveNote id. null for single
   * notes/rests. Shared by the per-notehead colouring (makeNoteColor) and the
   * per-notehead pulse (pulseMatchedNote).
   */
  protected chordHeadInfoForNote(note: NoteEvent): { rank: number; size: number } | null {
    const staveId = note.Graphics?.[0]?.staveNote?.id;
    const bucket = note.EventBucket;
    if (staveId == null || !bucket) return null;
    const chordMidis = bucket.notes
      .filter((n) => n.Graphics?.[0]?.staveNote?.id === staveId)
      .map((n) => n.MidiNote);
    if (chordMidis.length <= 1) return null;
    return { rank: chordHeadRankAscending(note.MidiNote, chordMidis), size: chordMidis.length };
  }

  private makeNoteColor(
    note: NoteEvent | RestEvent,
    eventState: MidiEventState,
    phase: 'onset' | 'offset',
    playedMidiNote: number | null = null,
    playedTimestampOverride?: number | null,
  ) {
    this.serErrorScore(note, eventState);
    // Full error (red) is terminal for visualization: later ADDITIONAL_NOTE, timing, etc. must not
    // repaint the note yellow/green while onset or offset remains WRONG_NOTE / NEVER_PLAYED.
    const lockedFullError = isErrorState(note.OnsetState) || isErrorState(note.OffsetState);
    // MUS-372: a semi-error (yellow) is likewise terminal against green. Once a note has been
    // graded early/late on either onset or offset, a subsequent clean grade on the other side
    // must leave it yellow — the colour has to keep matching the 0.7 it now scores. Red still
    // wins over yellow, which is why this is checked second.
    const lockedSemiError = isSemiErrorState(note.OnsetState) || isSemiErrorState(note.OffsetState);
    let paintState = eventState;
    if (lockedFullError && !isErrorState(eventState)) {
      paintState = isErrorState(note.OnsetState) ? note.OnsetState : note.OffsetState;
    } else if (lockedSemiError && eventState === MidiEventState.HIT) {
      paintState = isSemiErrorState(note.OnsetState) ? note.OnsetState : note.OffsetState;
    }

    // Same-hand chord: colour only THIS note's own notehead. A same-hand chord
    // is one VexFlow StaveNote whose noteheads share a single NoteContainer, so
    // we pass this note's ascending-pitch rank among the notes sharing its
    // StaveNote id (the chord). Left undefined for single notes (chordSize 1) and
    // rests, which fall through to whole-container colouring, unchanged.
    let chordHeadRank: number | undefined;
    let chordSize: number | undefined;
    if (note instanceof NoteEvent) {
      const info = this.chordHeadInfoForNote(note);
      if (info) {
        chordHeadRank = info.rank;
        chordSize = info.size;
      }
    }

    if (paintState === MidiEventState.HIT) {
      // Progressive green for a folded tie: colour only the heads the cursor has
      // reached. Untied notes (and single-head ties) have no HeadTimestamps and
      // colour their whole graphics array, exactly as before. Yellow/red below
      // stay whole-array, so a premature release / wrong note paints ALL heads.
      if (note instanceof NoteEvent && note.HeadTimestamps && note.HeadTimestamps.length > 1) {
        this.paintTieGreenSubset(note, this.getLatencyAdjustedTimestamp());
      } else {
        this.makeNoteCorrect(
          note.Graphics,
          this.colorblindType,
          note.Timestamp,
          chordHeadRank,
          chordSize,
        );
      }
    } else if (midiSemiErrorStates.contains(paintState)) {
      this.makeNoteSemiError(
        note.Graphics,
        this.colorblindType,
        note.Timestamp,
        chordHeadRank,
        chordSize,
      );
    } else if (midiFullErrorStates.contains(paintState)) {
      this.makeNoteError(
        note.Graphics,
        this.colorblindType,
        note.Timestamp,
        chordHeadRank,
        chordSize,
      );
    }

    if (note instanceof NoteEvent) {
      emitLocalAudioGradeEvent({
        eventId: `${note.PhraseRef?.uuid ?? 'phrase'}:${note.Timestamp}:${note.MidiNote}:${note.Hand}`,
        midiNote: note.MidiNote,
        playedMidiNote,
        phase,
        scoreTimestamp: note.Timestamp,
        expectedOffsetTimestamp: note.Timestamp + note.Length,
        noteLength: note.Length,
        hand: note.Hand,
        playedTimestamp:
          playedTimestampOverride !== undefined
            ? playedTimestampOverride
            : phase === 'onset'
              ? note.PlayedTimestamp
              : note.OffsetTimestamp,
        state: paintState,
      });
    }
  }

  // markOffsetAccuracy does not call markAccuracy
  private setOffsetAccuracy(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number | null = null,
  ) {
    this.makeNoteColor(note, eventState, 'offset', null, timestamp);
    note.OffsetState = eventState;
    note.SnapshotPhraseAccuracy = this.calcAccuracy();
    // Stamp the per-hand running accuracy too (MUS-468). This path grades
    // WRONG_NOTE / some NEVER_PLAYED notes; the hand-split path already stamps
    // LH/RH, and calcLH/RHAccuracy are rolling-window reads that hold a live
    // on-screen value at every note regardless of the current note's hand.
    // Without these, those note classes recorded overall accuracy but null
    // LH/RH - an on-screen value that was displayed but dropped. Pure reads;
    // no gameplay effect.
    note.LhSnapshotPhraseAccuracy = this.calcLHAccuracy();
    note.RhSnapshotPhraseAccuracy = this.calcRHAccuracy();
    note.SnapshotBPM = this.timeKeeper.getBpm();
    note.FreePlay = this.freePlayMode;
  }

  protected markOnsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number,
    pulse: boolean = false,
    midiNote: number | null,
  ) {
    note.PlayedTimestamp = timestamp;
    if (eventState !== MidiEventState.ADDITIONAL_NOTE && midiNote) {
      this.activeOnsetEvents[midiNote] = note;
      this.activeOnsetInputSources[midiNote] = this.pendingInputSourceByMidi.get(midiNote) ?? null;
    }
    // only pulse on correct. At least that's how the code reads currently.
    if (eventState === MidiEventState.HIT && pulse) {
      this.pulseMatchedNote(note, ERROR_REC_CLASS.CORRECT, note.Timestamp);
    }
    this.markAccuracyBothHands(note, eventState);
    // no need to set state for additional note, because additional note is a separate event
    if (eventState !== MidiEventState.ADDITIONAL_NOTE) {
      note.OnsetState = eventState;
    }
    this.makeNoteColor(note, eventState, 'onset', midiNote);
    if (
      (eventState === MidiEventState.ADDITIONAL_NOTE || eventState === MidiEventState.WRONG_NOTE) &&
      midiNote &&
      note.AdditionalNote === null
    ) {
      const additionalNoteEvent = new AdditionalNoteEvent(
        note.EventBucket,
        note.Timestamp,
        timestamp,
        midiNote,
        note.Hand,
      );
      note.AdditionalNote = additionalNoteEvent;
    }
    let pushedAdditionalNote: AdditionalNoteEvent | null = null;
    if (eventState === MidiEventState.ADDITIONAL_NOTE && midiNote) {
      const additionalNoteEvent = new AdditionalNoteEvent(
        note.EventBucket,
        note.Timestamp,
        timestamp,
        midiNote,
        note.Hand,
      );
      note.EventBucket.pushAdditionalNote(additionalNoteEvent);
      this.activeOnsetEvents[midiNote] = additionalNoteEvent;
      this.activeOnsetInputSources[midiNote] = this.pendingInputSourceByMidi.get(midiNote) ?? null;
      pushedAdditionalNote = additionalNoteEvent;
    }
    // PPC: a yellow/red onset inside the active loop range breaks perfection.
    this.notifyIfLoopPerfectionBroken(note, eventState, pushedAdditionalNote);

    // A genuinely wrong/extra note played while another note is still held should
    // also penalise that held note (yellow) - e.g. tapping a wrong note midway
    // through a long tie. The normal attribution above only reaches the note whose
    // onset window is near "now", which a long sustain has already passed.
    if (
      eventState === MidiEventState.ADDITIONAL_NOTE ||
      eventState === MidiEventState.WRONG_NOTE
    ) {
      this.penalizeSustainedNotesOnExtra(timestamp, note, midiNote);
    }
  }

  /**
   * A wrong/extra input also penalises any note still being held (HIT, not yet
   * released) in the same hand, so tapping a wrong note at ANY point during a
   * hold turns that note yellow - including deep inside a long tie, which the
   * onset-window attribution can no longer reach. Only reached for real
   * wrong/extra pitches (correct notes and rolled chords route through the HIT
   * path, never here) and skips a note already penalised. AdditionalNote is set
   * BEFORE repainting so serErrorScore (inside makeNoteColor) stamps ErrorScore
   * and the progressive-tie-green guard keeps it yellow.
   */
  private penalizeSustainedNotesOnExtra(
    now: number,
    excludeNote: NoteEvent,
    wrongMidi: number | null,
  ): void {
    const hand = excludeNote.Hand;
    for (const active of this.activeOnsetEvents) {
      if (!(active instanceof NoteEvent)) continue;
      if (active === excludeNote) continue;
      if (active.Hand !== hand) continue;
      if (active.OnsetState !== MidiEventState.HIT) continue;
      if (active.OffsetState !== MidiEventState.UNSET) continue;
      if (active.AdditionalNote !== null) continue;
      if (!this.checkAccuracyNoteHand(active)) continue;
      active.AdditionalNote = new AdditionalNoteEvent(
        active.EventBucket,
        active.Timestamp,
        now,
        wrongMidi ?? active.MidiNote,
        active.Hand,
      );
      this.makeNoteColor(active, MidiEventState.ADDITIONAL_NOTE, 'onset', wrongMidi);
    }
  }

  protected markOffsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number | null,
  ) {
    this.makeNoteColor(note, eventState, 'offset', null, timestamp);
    note.OffsetState = eventState;
    note.LhSnapshotPhraseAccuracy = this.calcLHAccuracy();
    note.RhSnapshotPhraseAccuracy = this.calcRHAccuracy();
    note.SnapshotPhraseAccuracy = this.calcAccuracy();
    note.FreePlay = this.freePlayMode;
    note.OffsetTimestamp = timestamp;
    // PPC: an early/late release (EARLY_OFFSET / LATE_OFFSET) inside the active
    // loop range paints the note yellow and breaks perfection immediately.
    this.notifyIfLoopPerfectionBroken(note, eventState, null);
  }

  private markRestAccuracyWithSideEffects(
    rest: RestEvent,
    additionalNote: number,
    timestamp: number,
  ) {
    this.markAccuracyBothHands(rest, MidiEventState.ADDITIONAL_NOTE);
    makeNoteSemiError(rest.Graphics, this.colorblindType);
    const additionalNoteEvent = new AdditionalNoteEvent(
      rest.EventBucket,
      rest.Timestamp,
      timestamp,
      additionalNote,
      rest.Hand,
    );
    rest.AdditionalNote = additionalNoteEvent;
    rest.EventBucket.pushAdditionalNote(additionalNoteEvent);
    this.activeOnsetEvents[additionalNote] = additionalNoteEvent;
    // PPC: a note played during a rest inside the active loop range is a yellow
    // extra note and breaks perfection.
    this.notifyIfLoopPerfectionBroken(rest, MidiEventState.ADDITIONAL_NOTE, additionalNoteEvent);
  }

  calcAccuracyHelper(accuracyQueue: (NoteEvent | RestEvent | AdditionalNoteEvent)[]) {
    const accuracyReversed = accuracyQueue.slice().reverse();
    const accuracyLength = accuracyReversed.length;
    const numerator = sum(
      accuracyReversed.map((evt, indx) => {
        let sumTarget;
        if (evt.ErrorScore === 0) {
          // sumTarget = (accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx) >= 0 ? ((accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx)) : 0
          return accuracyLength - indx;
        } else if (evt.ErrorScore < 1) {
          // start subtracting from top of magnitude... i.e. 40 * .5 is 20 so 20, 19, 18...
          // and when we get to zero
          sumTarget =
            (accuracyLength - indx) * this.correctNoteMagnitude >= 0
              ? (accuracyLength - indx) * this.correctNoteMagnitude
              : 0;
          return sumTarget * this.offNoteMagnitude;
        } else {
          return 0;
        }
      }),
    );
    const denominator = sum(
      accuracyReversed.map((evt, indx) => {
        let sumTarget;

        if (evt.ErrorScore === 0) {
          // sumTarget = (accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx) >= 0 ? ((accuracyLength * this.correctNoteMagnitude) - (accuracyLength - indx)) : 0
          return accuracyLength - indx;
        } else if (evt.ErrorScore < 1) {
          // start subtracting from top of magnitude... i.e. 40 * .5 is 20 so 20, 19, 18...
          // and when we get to zero
          sumTarget =
            (accuracyLength - indx) * this.correctNoteMagnitude >= 0
              ? (accuracyLength - indx) * this.correctNoteMagnitude
              : 0;
          return sumTarget;
        } else {
          // same same but different
          sumTarget =
            (accuracyLength - indx) * this.missMagnitude >= 0
              ? (accuracyLength - indx) * this.missMagnitude
              : 0;
          return sumTarget;
        }
      }),
    );
    // logger.info("returning accuracy " + (numerator / denominator) * 100)
    return (numerator / denominator) * 100;
  }

  calcAccuracy() {
    // logger.info("accuracy queue: ", this.accuracyQueue)
    const acc = this.calcAccuracyHelper(this.accuracyQueue);
    // logger.info("accuracy=", acc)
    // logger.info(this.accuracyQueue)
    return acc;
  }

  calcLHAccuracy() {
    // logger.info("lh accuracy queue: ", this.lhAccuracyQueue)
    const lhAccuracy = this.calcAccuracyHelper(this.lhAccuracyQueue);
    // logger.info("lh accuracy", lhAccuracy)
    return lhAccuracy;
  }

  calcRHAccuracy() {
    // logger.info("rh accuracy queue ", this.rhAccuracyQueue)
    const rhAccuracy = this.calcAccuracyHelper(this.rhAccuracyQueue);
    // logger.info("rh accuracy", rhAccuracy)
    return rhAccuracy;
  }

  /**
   * Evaluate note correctness within a loop bracket range. Used by the
   * perfect-phrase counter to decide whether every note in the looped section
   * was played fully "green" (a clean HIT on both onset and release).
   *
   * A range counts as perfect only at EXACTLY 100% accuracy: every expected
   * note must have a green (HIT) onset and a release that is green (HIT) or
   * still ungraded (UNSET), with no additional (extra/wrong) notes and no
   * timing semi-errors in the range. A note rendered yellow — a late/early
   * onset OR an actively mistimed release (EARLY_OFFSET / LATE_OFFSET) — breaks
   * perfection, matching the on-screen note colour. An UNSET release is
   * tolerated because the loop's final note ends at the wind-back: when the
   * user loops straight into the next iteration its release is often ungraded
   * at the boundary, and that must not fail a phrase played cleanly. Notes with
   * an UNSET onset (not yet reached/graded) are neither hits nor errors.
   *
   * Notes belonging to a hand whose accuracy is turned off are ignored
   * entirely (they're never graded and never coloured), so a loop can be
   * perfected by playing only the active hand — matching what's shown on screen.
   *
   * Buckets are matched on their onset timestamp, half-open [start, end) to
   * match the loop's exclusive end boundary.
   */
  loopRangeAccuracy(
    startTimestamp: number,
    endTimestamp: number,
    debug = false,
  ): { totalNotes: number; hitNotes: number; hasError: boolean } {
    let totalNotes = 0;
    let hitNotes = 0;
    let hasError = false;
    // [PPC] debug-only breakdowns, populated when `debug` is set (boundary
    // evaluation only, never the ~60 Hz poll).
    const debugNonHits: string[] = [];
    const debugExtras: string[] = [];

    const isOffTimeOrWrong = (state: MidiEventState) =>
      isErrorState(state) || isSemiErrorState(state);

    // Tolerance for a valid early start of the NEXT iteration. When the user
    // begins the next loop a hair early — playing the loop's first note within
    // the flexible early-onset margin before the wind-back, before the cursor
    // reaches the loop end — that note lands as an extra note inside the
    // still-open [start, end) range. It belongs to the next iteration's
    // count-in, not the loop just completed, so counting it as an error would
    // deny a point the user earned by playing the whole loop cleanly (MUS-335).
    // Recognise it by pitch (matches the loop's first note) AND timing (played
    // within the early-onset cushion before the loop end).
    const earlyStart = this.computeLoopEarlyStartAllowance(startTimestamp, endTimestamp);

    for (const bucket of this.eventBuckets) {
      if (bucket.timestamp < startTimestamp || bucket.timestamp >= endTimestamp) continue;

      for (const note of bucket.notes) {
        // Skip notes on a hand whose accuracy is disabled: they're never graded
        // (stay UNSET) or coloured, so they must not count toward the total.
        if (!this.checkAccuracyNoteHand(note)) continue;
        totalNotes++;
        // A note counts as a hit when its onset is green (HIT) and its release
        // is either green (HIT) or not-yet-graded (UNSET). The loop's final note
        // ends at the wind-back, so when the user loops straight into the next
        // iteration its release is often still ungraded at the boundary — that
        // must not fail a phrase played cleanly. A genuinely mistimed release
        // grades EARLY_OFFSET / LATE_OFFSET (a semi-error), which still trips
        // hasError below and fails the phrase.
        const isHit =
          note.OnsetState === MidiEventState.HIT &&
          (note.OffsetState === MidiEventState.HIT || note.OffsetState === MidiEventState.UNSET);
        if (isHit) {
          hitNotes++;
        }
        // Any off-time/wrong onset OR offset paints the note yellow/red and
        // breaks perfection. UNSET = not yet graded; neither a hit nor an error.
        if (isOffTimeOrWrong(note.OnsetState) || isOffTimeOrWrong(note.OffsetState)) {
          hasError = true;
        }
        if (debug && !isHit) {
          debugNonHits.push(
            `ts=${bucket.timestamp} midi=${note.MidiNote} onset=${note.OnsetState} offset=${note.OffsetState}`,
          );
        }
      }

      // Extra/wrong notes the user played in this range break perfection —
      // except on a hand whose accuracy is disabled (that hand isn't evaluated)
      // or a valid anticipatory early start of the next iteration (see above).
      if (
        bucket.additinalNotes.some(
          (extra) =>
            this.checkAccuracyNoteHand(extra) && !this.isAnticipatoryEarlyStart(extra, earlyStart),
        )
      ) {
        hasError = true;
      }
      if (debug) {
        for (const extra of bucket.additinalNotes) {
          if (!this.checkAccuracyNoteHand(extra)) continue;
          debugExtras.push(
            `playedMidi=${extra.PlayedNote} playedTs=${extra.PlayedTimestamp} forgivenEarlyStart=${this.isAnticipatoryEarlyStart(extra, earlyStart)}`,
          );
        }
      }
    }

    if (debug) {
      const allowance = earlyStart
        ? `{boundary=${earlyStart.boundary} pitches=[${[...earlyStart.pitches].join(',')}]}`
        : 'none';
      console.log(
        `[PPC] evaluate range=[${startTimestamp},${endTimestamp}) verdict totalNotes=${totalNotes} hitNotes=${hitNotes} hasError=${hasError} ` +
          `earlyStartAllowance=${allowance} bpm=${this.timeKeeper.getBpm()} ` +
          `nonHits=[${debugNonHits.join(' | ')}] extras=[${debugExtras.join(' | ')}]`,
      );
    }

    return { totalNotes, hitNotes, hasError };
  }

  /**
   * Compute the pitch set and timing boundary that identify a valid "early
   * start" of the NEXT loop iteration: the loop's first note(s) replayed within
   * the flexible early-onset margin just before the loop end (the wrap point the
   * next iteration's first note onsets at). Returns null when the loop has no
   * gradable first note or the tempo isn't known, in which case no extra note is
   * forgiven. See loopRangeAccuracy.
   */
  private computeLoopEarlyStartAllowance(
    startTimestamp: number,
    endTimestamp: number,
  ): { pitches: Set<number>; boundary: number } | null {
    const bpm = this.timeKeeper.getBpm();
    if (!bpm || bpm <= 0) return null;

    // The loop's first note = the earliest in-range bucket with any note on an
    // accuracy-enabled hand.
    const firstBucket = this.eventBuckets.find(
      (bucket) =>
        bucket.timestamp >= startTimestamp &&
        bucket.timestamp < endTimestamp &&
        bucket.notes.some((note) => this.checkAccuracyNoteHand(note)),
    );
    if (!firstBucket) return null;

    const firstNotes = firstBucket.notes.filter((note) => this.checkAccuracyNoteHand(note));
    const pitches = new Set(firstNotes.map((note) => note.MidiNote));

    // Mirror handleNoteOnEvent's early-onset window, anchored at the loop end.
    // Use the first note's static green-zone window so the BPM clamp matches the
    // one note-matching applies when it grades an early onset.
    const firstNote = firstNotes[0];
    const staticWindowOSMD =
      firstNote.Timestamp - firstNote.NoteOnTimingOffsets.correctStartTimestamp;
    const boundary = bpmAdjustedEarlyOnsetBoundary(
      endTimestamp,
      endTimestamp - staticWindowOSMD,
      bpm,
    );
    return { pitches, boundary };
  }

  /**
   * Whether an extra note is the anticipatory first note of the next loop
   * iteration: same pitch as the loop's first note and played within the
   * early-onset cushion before the loop end. Such notes belong to the next
   * iteration's count-in, not the loop just completed, so loopRangeAccuracy must
   * not count them as errors.
   */
  private isAnticipatoryEarlyStart(
    extra: AdditionalNoteEvent,
    allowance: { pitches: Set<number>; boundary: number } | null,
  ): boolean {
    if (!allowance) return false;
    const playedAt = extra.PlayedTimestamp;
    return (
      playedAt !== null && playedAt >= allowance.boundary && allowance.pitches.has(extra.PlayedNote)
    );
  }

  calcRepAccuracy(tempo: number, performanceTempo: number): [number, number[]] {
    // I had to bring this logic of "setAccuracyAtTempo" inside the midi stream, because in the context
    // "midiStream.accuracyQueue" does not update unless some other action causes re-render
    //  (you can observe by logging "midiStream.accuracyQueue" in the RepertoirePlayContext vs logging "this.accuracyQueue" here in EventStream)
    const accuracyLength = this.accuracyQueue.length;
    const evaluatedNotes = this.accuracyQueue.filter(
      (note) => note.OnsetState !== MidiEventState.ADDITIONAL_NOTE,
    );
    const accuracyReversed = evaluatedNotes.slice().reverse();
    const denominator = evaluatedNotes.length;
    const numerator = denominator - sum(accuracyReversed.map((evt) => evt.ErrorScore));
    const accuracyAtTempo = evaluatedNotes.map((v) => {
      const performanceFac = Math.min(tempo / performanceTempo, 1);
      if (v.ErrorScore == 1) {
        return 0;
      } else if (v.ErrorScore == 0) {
        return performanceFac;
      } else {
        // ErrorScore is the ERROR portion (0 = correct, 0.7 = semi-error, 1 = full error).
        // Invert to get the CORRECT portion, matching how rawAccuracy uses (1 - ErrorScore).
        return (1 - v.ErrorScore) * performanceFac;
      }
    });

    // NaN is the established "no evaluated notes yet" sentinel. The shared
    // repertoire context uses it to avoid overwriting OSMD's accuracy before
    // the first note is evaluated.
    const result = (numerator / denominator) * 100;
    logger.debug('[ACC-TRACE] calcRepAccuracy', {
      streamId: this.id,
      accuracyLength,
      numerator,
      denominator,
      result,
      isNaN: isNaN(result),
      tempo,
      performanceTempo,
      totalNotesLength: this.totalNotesLength(),
      sampleOnsetStates: this.accuracyQueue.slice(0, 5).map((evt) => ({
        onset: evt.OnsetState,
        error: evt.ErrorScore,
      })),
    });

    return [result, accuracyAtTempo];
  }

  private setupOnsetEvents() {
    // There are 128 MIDI event pitches.
    // Allocate a fixed size array with 128 slots,
    // and incoming MIDI on/off events will be "mapped" using their pitch into this array.
    this.activeOnsetEvents = new Array(128);
    this.activeOnsetEvents.fill(null);
    // See: https://stackoverflow.com/a/44853951
    Object.seal(this.activeOnsetEvents);
    this.activeOnsetInputSources = new Array(128);
    this.activeOnsetInputSources.fill(null);
    Object.seal(this.activeOnsetInputSources);
    this.pendingInputSourceByMidi.clear();

    let str = '';
    for (let i = 0; i <= 24; i++) {
      const x = (1 / 24) * i;
      str = str + `\n${4 * x}, ${4 * this.calculateCorrectNoteOffMin(x)}`;
    }
  }

  reset() {
    logger.info('resetting event stream');
    this.eventBuckets = [];
    this.lastProcessedTimestamp = -1;
    this.onsetIndex = 0;
    this.missedNotesIndex = 0;
    this._realNotesMarkedCount = 0;
    // Flush the duplicate-note-on guard so a restart/start doesn't carry a stale
    // accepted-time anchor from the previous run into the fresh event stream.
    this.lastNoteOnTimestampByPitchVelocity.clear();
    this.setupOnsetEvents();
    this.initAccuracyQueues();
    // this.accuracyQueue = []
  }

  /**
   * Seek the event stream's onset index to the bucket at or just before the
   * given timestamp. This allows loop restart to reprocess notes from a
   * specific point without clearing all event buckets.
   *
   * Also resets the missedNotesIndex so that notes within the looped region
   * are re-evaluated on the next pass.
   */
  seekToTimestamp(timestamp: number): void {
    console.log(
      `[LOOP-DBG] EventStream.seekToTimestamp timestamp=${timestamp} totalBuckets=${this.eventBuckets.length} loopStart=${this.loopStartTimestamp} loopEnd=${this.loopEndTimestamp} id=${this.id}`,
    );

    // Use bucket.timestamp (the actual note onset) rather than
    // bucket.minOnsetTimestamp (which includes the early-hit timing window
    // and can be negative for the first bucket).
    const targetIndex = this.eventBuckets.findIndex((bucket) => bucket.timestamp >= timestamp);

    // If no bucket is at or after the timestamp, seek to the end.
    this.onsetIndex = targetIndex === -1 ? this.eventBuckets.length : targetIndex;
    this.missedNotesIndex = this.onsetIndex;
    this.lastProcessedTimestamp = timestamp;

    console.log(
      `[LOOP-DBG] EventStream.seekToTimestamp complete onsetIndex=${this.onsetIndex} missedNotesIndex=${this.missedNotesIndex} firstBucketMinOnset=${this.eventBuckets[0]?.minOnsetTimestamp}`,
    );
  }

  addEvents2(iterator: Iterator | IteratorPhaser, startMeasure: number) {
    iterator.resetIterator();

    // Track pitches whose tie-START we actually emitted, so a continuation only
    // folds when its start is really in the stream. If a start was dropped
    // upstream (e.g. a note skipped for missing positionAndShape), its orphaned
    // continuation grades per-note instead of silently vanishing the whole tie.
    const emittedTieStarts = new Set<number>();

    while (!iterator.EndReached) {
      const notes: NoteEvent[] = [];
      const rests: RestEvent[] = [];
      const onsetTimestamp = startMeasure + iterator.currentTimeStamp;
      // Check if there's already a bucket for this timestamp.
      // OSMD's iterator WILL repeat timestamps.
      let bucket = this.eventBuckets.find(
        (b) => Math.abs(b.timestamp - onsetTimestamp) < Number.EPSILON,
      );
      const isNewBucket = bucket === undefined;
      if (isNewBucket) {
        // Registration is DEFERRED until we know the slice produced >= 1 event.
        // Tie-folding can leave a continuation-only slice with no notes/rests; an
        // empty bucket must never be registered - it would dilute bucket-count
        // consumers (e.g. Phrase.getAvgBpm divides by playData.length) and carry
        // meaningless onset bounds.
        bucket = new EventBucket(onsetTimestamp, [], [], iterator.phrase);
      }

      iterator.CurrentVoiceEntries.forEach((currentVoiceEntry) => {
        for (const note of currentVoiceEntry.Notes) {
          // Logger.info("adding note", note)
          // Tie handling on the prerendered path. This mirrors the live-OSMD
          // calculateTiedNoteLength(): the FIRST note of a tie group carries the
          // whole tie as one sustained expected-note (held for the combined
          // duration, struck once), and every continuation is folded into it and
          // discarded so it is not graded as a separate onset. The prerenderer
          // (musicxml-tagging) supplies note.noteTie.{isTieStart,tieDuration}.
          // Fold a tie continuation into its tie-start ONLY when positively
          // identified (isTieStart === false) AND its start was actually emitted
          // into this stream. A payload that carries tie info but no isTieStart
          // (an older prerender shape) is not treated as a continuation, so it
          // degrades to per-note grading rather than dropping the tie; and an
          // orphaned continuation (start dropped upstream) also grades per-note
          // rather than folding into nothing.
          const isTieStart = note.noteTie?.isTieStart === true;
          const tiePitchKey = !note.isRest && note.pitch ? note.pitch.halfTone + 12 : null;
          if (
            note.noteTie?.isTieStart === false &&
            tiePitchKey !== null &&
            emittedTieStarts.has(tiePitchKey)
          ) {
            continue; // continuation: represented by the tie-start's held note
          }
          const tieDuration = note.noteTie?.tieDuration;
          const noteDuration =
            isTieStart && typeof tieDuration === 'number' && tieDuration > 0
              ? tieDuration
              : note.length;
          if (noteDuration <= 0) {
            continue;
          }
          const offsetTimestamp = onsetTimestamp + noteDuration;
          const correctNoteOffsetStartTimestamp =
            onsetTimestamp + this.calculateCorrectNoteOffMin(noteDuration);

          const startAllowance = 0.25;
          const endAllowance = 0.25;

          if (note.isRest) {
            const restEvent = new RestEvent(
              bucket as EventBucket,
              onsetTimestamp,
              new TimingOffsets(
                onsetTimestamp === 0
                  ? -0.2
                  : onsetTimestamp - startAllowance * this.offsetsConfig.correctRestOnsetPct,
                offsetTimestamp - endAllowance * this.offsetsConfig.correctRestOffsetPct,
                onsetTimestamp === 0
                  ? -0.2
                  : onsetTimestamp - startAllowance * this.offsetsConfig.mistimedRestOnsetPct,
                offsetTimestamp - endAllowance * this.offsetsConfig.mistimedRestOffsetPct,
              ),
              note.graphics,
              note.length,
              !note.printObject,
              note.staffPosition === 'bottom' ? HANDEDNESS.LEFT : HANDEDNESS.RIGHT,
            );
            rests.push(restEvent);
          } else {
            const noteOnTimestamps = new TimingOffsets(
              onsetTimestamp === 0
                ? -0.2
                : onsetTimestamp - startAllowance * this.offsetsConfig.correctNoteOnsetStartPct,
              onsetTimestamp + endAllowance * this.offsetsConfig.correctNoteOnsetEndPct,
              onsetTimestamp === 0
                ? -0.2
                : onsetTimestamp - startAllowance * this.offsetsConfig.mistimedNoteOnsetStartPct,
              onsetTimestamp + endAllowance * this.offsetsConfig.mistimedNoteOnsetEndPct,
            );

            const noteOffTimestamps = new TimingOffsets(
              correctNoteOffsetStartTimestamp,
              offsetTimestamp + endAllowance * this.offsetsConfig.correctNoteOffsetEndPct,
              correctNoteOffsetStartTimestamp - noteDuration * 0.2,
              offsetTimestamp + endAllowance * this.offsetsConfig.mistimedNoteOffsetEndPct,
            );

            // Notehead graphics for the (possibly folded) note. Two shapes reach
            // here. Path A (serialized prerender): the prerenderer already merged
            // every tied head's graphics onto `note.graphics`, and carries no
            // per-head `heads` list - so the whole tie highlights together (all
            // states simultaneous), unchanged. Path B (live OSMD / repertoire):
            // `note.graphics` is only the start head, and the tie-start carries an
            // ordered `heads` list (graphic + phrase-relative timestamp). We merge
            // every head's graphic onto this one held event AND record each head's
            // bucket-space timestamp (rebased off the start head, so it is
            // offset-invariant) so the sustain path can green heads progressively
            // while yellow/red still colour all heads at once.
            const tieHeads = note.noteTie?.heads;
            let graphics: PrerenderedGraphics[];
            let headTimestamps: number[] | undefined;
            if (isTieStart && tieHeads && tieHeads.length > 1) {
              const startHeadTimestamp = tieHeads[0].timestamp;
              graphics = tieHeads.map((head) => head.graphic);
              headTimestamps = tieHeads.map(
                (head) => onsetTimestamp + (head.timestamp - startHeadTimestamp),
              );
            } else {
              graphics = note.graphics;
              headTimestamps = undefined;
            }
            if (note.staffPosition === 'unknown') {
              Logger.warn('unknown staff position on note');
            }
            const midiNote = note.pitch.halfTone + 12;
            // Ignore a duplicate add: the same pitch already present in this
            // bucket (same note AND time information). OSMD's iterator can repeat
            // timestamps, and a restart/start that re-adds events onto a stream
            // that wasn't fully flushed would otherwise stack a second copy of
            // every note in the bucket — leaving the duplicate UNSET and
            // mis-scoring the phrase. Check both the notes already committed to
            // the bucket and those staged in this pass.
            const isDuplicateAdd =
              bucket.notes.some((existing) => existing.MidiNote === midiNote) ||
              notes.some((staged) => staged.MidiNote === midiNote);
            if (isDuplicateAdd) {
              Logger.info('[EventStream.addEvents2] skipping duplicate note add', {
                midiNote,
                onsetTimestamp,
              });
              continue;
            }
            const noteEvent = new NoteEvent(
              bucket as EventBucket,
              onsetTimestamp,
              midiNote,
              noteOnTimestamps,
              noteOffTimestamps,
              graphics,
              noteDuration,
              iterator.phrase,
              note.staffPosition === 'bottom' ? HANDEDNESS.LEFT : HANDEDNESS.RIGHT,
            );
            noteEvent.HeadTimestamps = headTimestamps;
            // Logger.info("adding note event", noteEvent)
            notes.push(noteEvent);
            if (isTieStart && tiePitchKey !== null) {
              emittedTieStarts.add(tiePitchKey);
            }
          }
        }
      });
      bucket.addEvents(notes, rests);
      if (isNewBucket && (bucket.notes.length > 0 || bucket.rests.length > 0)) {
        iterator?.phrase?.pushPhrasePlayData(bucket);
        this.eventBuckets.push(bucket);
      }
      // A newly-created bucket left empty by tie-folding is simply dropped here
      // (never registered in eventBuckets or phrase.playData).
      iterator.next();
    }

    // Tell all the event buckets to sort their internals and then we'll sort the buckets themselves.
    this.eventBuckets.forEach((b) => b.sort());
    this.eventBuckets.sort((b1, b2) => b1.minOnsetTimestamp - b2.minOnsetTimestamp);

    // Debug logging for event addition
    Logger.info(
      '[EventStream.addEvents2] Added events for phrase at startMeasure:',
      startMeasure,
      'totalBuckets:',
      this.eventBuckets.length,
      'currentOnsetIndex:',
      this.onsetIndex,
    );
    if (this.eventBuckets.length > 0) {
      const firstBucket = this.eventBuckets[0];
      const lastBucket = this.eventBuckets[this.eventBuckets.length - 1];
      Logger.info(
        '[EventStream.addEvents2] Bucket range - first:',
        firstBucket.timestamp,
        'last:',
        lastBucket.timestamp,
      );
    }

    if (iterator.phrase) {
      this.phraseHistory.push(iterator.phrase);
    }
  }

  // this function returns the last phrase N before the current phrase - starting with 1 being the phrase
  // directly before the current phrase
  getNBeforePhrase = (nBefore: number): Phrase | undefined => {
    let currentIndex = this.onsetIndex;
    let nPhrases = 0;
    const currentPhrase = this.eventBuckets[currentIndex]?.phraseRef;
    if (currentPhrase) {
      while (nPhrases < nBefore && currentPhrase) {
        if (!(this.eventBuckets[currentIndex]?.phraseRef?.uuid == currentPhrase.uuid)) {
          nPhrases++;
        }
        currentIndex--;
      }
      return this.eventBuckets[currentIndex]?.phraseRef;
    } else {
      return this.eventBuckets?.last()?.phraseRef;
    }
  };

  // God speed to any who dare edit below this line.
  /**
   * Whether a note-on at `timestamp` (musical/measure time) duplicates one just
   * accepted for the same pitch AND velocity — i.e. a hardware/driver double-
   * trigger rather than a genuine re-articulation. Two identical note-ons that
   * land within {@link DUPLICATE_NOTE_ON_MIN_GAP_SEC} of each other are treated
   * as the same physical keypress; converting the OSMD-unit gap to real seconds
   * via the tempo keeps the threshold BPM-independent. Distinct pitches or
   * velocities never collide. When a duplicate is detected the accepted-time
   * anchor is left unchanged so a burst of glitch copies all collapse to the
   * first accepted event.
   */
  private isDuplicateNoteOn(midiNote: number, velocity: number, timestamp: number): boolean {
    const key = `${midiNote}:${velocity}`;
    const lastTimestamp = this.lastNoteOnTimestampByPitchVelocity.get(key);
    if (lastTimestamp !== undefined) {
      const bpm = this.timeKeeper.getBpm();
      const gapSec =
        bpm > 0
          ? (Math.abs(timestamp - lastTimestamp) * 240) / bpm
          : Math.abs(timestamp - lastTimestamp);
      if (gapSec < DUPLICATE_NOTE_ON_MIN_GAP_SEC) {
        return true;
      }
    }
    this.lastNoteOnTimestampByPitchVelocity.set(key, timestamp);
    return false;
  }

  handleNoteOnEvent(midiNote: number, velocity: number, inputTiming?: EventInputTiming) {
    logger.debug('[OSMD-MIDI] EventStream.handleNoteOnEvent ENTERED', {
      id: this.id,
      midiNote,
      velocity,
      constructorName: this.constructor.name,
      errorRecognitionActive: this.errorRecognitionActive,
      hasTimeKeeper: this.timeKeeper !== undefined && this.timeKeeper !== null,
    });
    logger.debug('note on in midi stream: ', midiNote);
    if (!this.errorRecognitionActive) {
      logger.debug(
        `[NOTE-REGISTER-DBG] EventStream.handleNoteOnEvent — RETURNING: errorRecognitionActive is falsy/null midiNote=${midiNote}`,
      );
      return;
    }
    if (!this.errorRecognitionActive.left && !this.errorRecognitionActive.right) {
      logger.debug(
        `[NOTE-REGISTER-DBG] EventStream.handleNoteOnEvent — WARNING: both hands disabled (errorRecLeft=false errorRecRight=false) midiNote=${midiNote} — note will be processed but no hand will accept it`,
      );
    }
    this.pendingInputSourceByMidi.set(midiNote, inputTiming?.source ?? null);
    const timestamp = this.getLatencyAdjustedTimestamp(inputTiming);
    logger.debug(
      `[LOOP-DBG] EventStream.handleNoteOnEvent midiNote=${midiNote} timestamp=${timestamp} onsetIndex=${this.onsetIndex} totalBuckets=${this.eventBuckets.length} isPaused=${this.timeKeeper.isPaused()} errorRecLeft=${this.errorRecognitionActive.left} errorRecRight=${this.errorRecognitionActive.right} loopStart=${this.loopStartTimestamp} loopEnd=${this.loopEndTimestamp}`,
    );
    // Log the last few buckets near the current timestamp for debugging
    if (this.loopEndTimestamp !== null && this.eventBuckets.length > 0) {
      const nearEnd = this.eventBuckets
        .map((b, idx) => ({ idx, ts: b.timestamp, notes: b.notes.map((n) => n.MidiNote) }))
        .filter((b) => b.ts >= timestamp - 1 && b.ts <= (this.loopEndTimestamp ?? 999))
        .slice(0, 5);
      logger.debug(`[LOOP-DBG] EventStream nearby buckets: ${JSON.stringify(nearEnd)}`);
    }
    if (this.timeKeeper.isPaused()) {
      logger.debug(`[LOOP-DBG] EventStream.handleNoteOnEvent — RETURNING: TK paused`);
      return;
    }
    // Drop glitchy duplicate note-on events (same pitch AND velocity arriving
    // within a gap no human could physically re-articulate — see
    // DUPLICATE_NOTE_ON_MIN_GAP_SEC). Left unchecked, the duplicate is matched
    // as an extra/"additional" note against the already-satisfied target and
    // repaints a cleanly-hit note yellow. This is the centralized choke point
    // for every note-on source (live MIDI, loop-restart replay, playback-start
    // queue), so it also protects against a single physical keypress fanned out
    // by stale/duplicate listeners.
    if (this.isDuplicateNoteOn(midiNote, velocity, timestamp)) {
      logger.debug('[EventStream] Ignoring duplicate note-on', { midiNote, velocity, timestamp });
      return;
    }
    let closestNoteMatch: NoteEvent | null = null;
    let closestPitchDiff: number = -1;
    let restMatches: RestEvent[] = [];
    let possibleNotes: NoteEvent[] = [];
    let exactMatchFound = false;
    let possibleNoteBuckets: EventBucket[] = [];
    let earlyOnset = false;
    let lateOnset = false;
    let latestOnsetIndex = 0;

    for (
      let i = this.onsetIndex > 0 ? this.onsetIndex - 1 : this.onsetIndex;
      i < this.eventBuckets.length;
      i++
    ) {
      const bucket = this.eventBuckets[i];

      // Skip buckets outside loop bounds (exclusive end — notes AT the
      // bracket end must not be matched during looping)
      if (this.loopStartTimestamp !== null && this.loopEndTimestamp !== null) {
        if (
          bucket.timestamp < this.loopStartTimestamp ||
          bucket.timestamp >= this.loopEndTimestamp
        ) {
          continue;
        }
      }

      const inBucket = this.isInBucket(timestamp, i);
      if (!inBucket) {
        // Log why it wasn't in bucket for first few iterations
        if (i < this.onsetIndex + 3) {
          Logger.info(
            '[EventStream] Bucket',
            i,
            'NOT in bucket - timestamp:',
            timestamp,
            'min:',
            bucket.minOnsetTimestamp,
            'max:',
            bucket.maxOffsetTimestamp,
          );
        }
        continue;
      } else if (timestamp < bucket.minOnsetTimestamp) {
        Logger.info('[EventStream] Breaking at bucket', i, '- timestamp < minOnset');
        break;
      }

      Logger.info('[EventStream] Found matching bucket', i, 'at timestamp', bucket.timestamp);

      latestOnsetIndex = i;
      possibleNotes = possibleNotes.concat(bucket.notes);
      possibleNoteBuckets = possibleNoteBuckets.concat(bucket);
      restMatches = restMatches.concat(bucket.rests);

      for (const noteEvent of bucket.notes) {
        if (
          timestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp ||
          timestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp
        ) {
          Logger.info(
            'timestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp',
            timestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp,
          );
          Logger.info(
            'timestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp',
            timestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp,
          );
          Logger.info('skipping noteEvent', noteEvent);
          continue;
        }

        const pitchDiff = Math.abs(noteEvent.MidiNote - midiNote);
        Logger.info('pitchDiff', pitchDiff);
        Logger.info('noteEvent.MidiNote', noteEvent.MidiNote);
        Logger.info('midiNote', midiNote);
        if (pitchDiff === 0) {
          closestNoteMatch = noteEvent;
          exactMatchFound = true;
          closestPitchDiff = 0;
          const adjustedEarlyBoundary = bpmAdjustedEarlyOnsetBoundary(
            noteEvent.Timestamp,
            noteEvent.NoteOnTimingOffsets.correctStartTimestamp,
            this.timeKeeper.getBpm(),
          );
          Logger.info(
            'timestamp < adjustedEarlyBoundary (BPM-adjusted correctStartTimestamp)',
            timestamp < adjustedEarlyBoundary,
          );
          if (timestamp < adjustedEarlyBoundary) {
            earlyOnset = true;
          }
          Logger.info(
            'timestamp > noteEvent.NoteOnTimingOffsets.correctEndTimestamp',
            timestamp > noteEvent.NoteOnTimingOffsets.correctEndTimestamp,
          );
          if (timestamp > noteEvent.NoteOnTimingOffsets.correctEndTimestamp) {
            lateOnset = true;
          }
        } else if (closestNoteMatch == null) {
          closestNoteMatch = noteEvent;
          closestPitchDiff = pitchDiff;
        } else {
          const closestMatchTimestampDiff = Math.abs(closestNoteMatch.Timestamp - timestamp);
          const timestampDiff = Math.abs(noteEvent.Timestamp - timestamp);
          if (closestPitchDiff >= pitchDiff && closestMatchTimestampDiff >= timestampDiff) {
            closestNoteMatch = noteEvent;
            closestPitchDiff = pitchDiff;
          }
        }
      }
    }
    possibleNotes.sort((a, b) => b.Timestamp - a.Timestamp);
    possibleNoteBuckets.sort(
      (eventBucket1, eventBucket2) =>
        Math.abs(eventBucket1.timestamp - timestamp) - Math.abs(eventBucket2.timestamp - timestamp),
    );

    const isAttemptedNoteInChord =
      possibleNoteBuckets.length > 0 && possibleNoteBuckets[0].notes.length > 1;
    console.log(
      `[NOTE-REGISTER-DBG] handleNoteOnEvent post-loop midiNote=${midiNote} timestamp=${timestamp} ` +
        `latestOnsetIndex=${latestOnsetIndex} onsetIndex=${this.onsetIndex} ` +
        `possibleNotes=${possibleNotes.length} possibleBuckets=${possibleNoteBuckets.length} ` +
        `restMatches=${restMatches.length} exactMatchFound=${exactMatchFound} ` +
        `closestPitchDiff=${closestPitchDiff} earlyOnset=${earlyOnset} lateOnset=${lateOnset} ` +
        `isAttemptedNoteInChord=${isAttemptedNoteInChord} ` +
        `willEnterMatchBlock=${latestOnsetIndex >= this.onsetIndex || isAttemptedNoteInChord}`,
    );
    if (latestOnsetIndex >= this.onsetIndex || isAttemptedNoteInChord) {
      Logger.info('latestOnsetIndex', latestOnsetIndex);
      Logger.info('this.onsetIndex', this.onsetIndex);
      Logger.info('isAttemptedNoteInChord', isAttemptedNoteInChord);
      Logger.info('exactMatchFound', exactMatchFound);
      Logger.info('closestNoteMatch', closestNoteMatch);
      if (
        closestNoteMatch &&
        exactMatchFound &&
        closestNoteMatch.OffsetState === MidiEventState.UNSET
      ) {
        const closestNoteBucket = possibleNoteBuckets[0];
        if (!this.checkAccuracyNoteHand(closestNoteMatch)) return;

        const isChord = closestNoteBucket.notes.length > 1;

        if (
          closestNoteMatch.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp &&
          closestNoteMatch.NoteOnTimingOffsets.mistimedEndTimestamp >= timestamp &&
          closestNoteMatch.OffsetState === MidiEventState.UNSET
        ) {
          Logger.info('closestNoteMatch', closestNoteMatch);
          if (closestNoteMatch.OnsetState === MidiEventState.UNSET) {
            if (!(earlyOnset || lateOnset)) {
              Logger.info('hit 1');
              this.markOnsetAccuracyWithSideEffects(
                closestNoteMatch,
                MidiEventState.HIT,
                timestamp,
                true,
                midiNote,
              );
            } else if (earlyOnset) {
              this.markOnsetAccuracyWithSideEffects(
                closestNoteMatch,
                MidiEventState.EARLY_ONSET,
                timestamp,
                true,
                midiNote,
              );
            } else if (lateOnset) {
              this.markOnsetAccuracyWithSideEffects(
                closestNoteMatch,
                MidiEventState.LATE_ONSET,
                timestamp,
                true,
                midiNote,
              );
            }
          } else if (
            !isErrorState(closestNoteMatch.OnsetState) ||
            (isErrorState(closestNoteMatch.OnsetState) && isChord)
          ) {
            this.markOnsetAccuracyWithSideEffects(
              closestNoteMatch,
              MidiEventState.ADDITIONAL_NOTE,
              timestamp,
              false,
              midiNote,
            );
          }
        }
      } else if (
        (closestNoteMatch != null &&
          possibleNotes.length == 1 &&
          !(
            possibleNoteBuckets[0].rests.length > 0 && possibleNoteBuckets[0].notes.length === 0
          )) ||
        (closestNoteMatch == null &&
          possibleNotes.length == 1 &&
          !(possibleNoteBuckets[0].rests.length > 0 && possibleNoteBuckets[0].notes.length === 0))
      ) {
        if (closestNoteMatch === null) {
          closestNoteMatch = possibleNotes[0];
        }
        if (!this.checkAccuracyNoteHand(closestNoteMatch)) return;

        if (closestNoteMatch.OffsetState === MidiEventState.UNSET) {
          if (
            closestNoteMatch.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp &&
            closestNoteMatch.NoteOnTimingOffsets.mistimedEndTimestamp >= timestamp
          ) {
            Logger.info('closestNoteMatch', closestNoteMatch);
            if (closestNoteMatch.OnsetState === MidiEventState.UNSET) {
              this.markOnsetAccuracyWithSideEffects(
                closestNoteMatch,
                MidiEventState.WRONG_NOTE,
                timestamp,
                false,
                midiNote,
              );
            } else if (closestNoteMatch.OnsetState === MidiEventState.HIT) {
              Logger.info('hit 2');
              this.markOnsetAccuracyWithSideEffects(
                closestNoteMatch,
                MidiEventState.ADDITIONAL_NOTE,
                timestamp,
                false,
                midiNote,
              );
            } else {
              this.markOnsetAccuracyWithSideEffects(
                closestNoteMatch,
                MidiEventState.ADDITIONAL_NOTE,
                timestamp,
                false,
                midiNote,
              );
            }
          } else {
            this.markOnsetAccuracyWithSideEffects(
              closestNoteMatch,
              MidiEventState.ADDITIONAL_NOTE,
              timestamp,
              false,
              midiNote,
            );
          }
        } else {
          this.markOnsetAccuracyWithSideEffects(
            closestNoteMatch,
            MidiEventState.ADDITIONAL_NOTE,
            timestamp,
            false,
            midiNote,
          );
        }
      } else if (possibleNotes.length > 1) {
        possibleNotes.sort(
          (note1, note2) =>
            Math.abs(midiNote - note1.MidiNote) - Math.abs(midiNote - note2.MidiNote),
        );
        const alreadyPlayedNote = possibleNotes.find((note) => {
          const inTimespan =
            note.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp &&
            note.NoteOffTimingOffsets.correctStartTimestamp >= timestamp;
          return (
            inTimespan &&
            note.OnsetState !== MidiEventState.UNSET &&
            note.OnsetState !== MidiEventState.NEVER_PLAYED &&
            !isErrorState(note.OnsetState) &&
            note.OffsetState === MidiEventState.UNSET
          );
        });
        let closestInBucket: NoteEvent | undefined = undefined;

        const allPlayed = every(possibleNotes, (note) => note.OnsetState !== MidiEventState.UNSET);
        if (!exactMatchFound && alreadyPlayedNote) {
          if (!this.checkAccuracyNoteHand(alreadyPlayedNote)) return;
          this.markOnsetAccuracyWithSideEffects(
            alreadyPlayedNote,
            MidiEventState.ADDITIONAL_NOTE,
            timestamp,
            false,
            midiNote,
          );
          closestNoteMatch = alreadyPlayedNote;
        } else if (allPlayed) {
          for (let i = 0; i < possibleNotes.length; i++) {
            const currNote = possibleNotes[i];
            const inTimespan =
              currNote.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp &&
              currNote.NoteOffTimingOffsets.correctStartTimestamp >= timestamp;
            exactMatchFound = currNote.MidiNote === midiNote;
            if (
              inTimespan &&
              (exactMatchFound ||
                (!isErrorState(currNote.OnsetState) &&
                  currNote.OffsetState === MidiEventState.UNSET))
            ) {
              closestInBucket = currNote;
              break;
            }
          }
          if (closestInBucket) {
            this.markOnsetAccuracyWithSideEffects(
              closestInBucket,
              MidiEventState.ADDITIONAL_NOTE,
              timestamp,
              false,
              midiNote,
            );
            closestNoteMatch = closestInBucket;
          }
        } else {
          for (let i = 0; i < possibleNotes.length; i++) {
            const currNote = possibleNotes[i];
            const inTimespan =
              currNote.NoteOnTimingOffsets.mistimedStartTimestamp <= timestamp &&
              currNote.NoteOffTimingOffsets.correctStartTimestamp >= timestamp;
            exactMatchFound = currNote.MidiNote === midiNote;
            if (
              inTimespan &&
              (exactMatchFound ||
                (currNote.OnsetState === MidiEventState.UNSET &&
                  currNote.OffsetState === MidiEventState.UNSET))
            ) {
              closestInBucket = currNote;
              closestNoteMatch = closestInBucket;
              break;
            }
          }
          if (closestInBucket && !this.checkAccuracyNoteHand(closestInBucket)) return;

          if (
            closestInBucket &&
            exactMatchFound &&
            closestInBucket.OffsetState === MidiEventState.UNSET
          ) {
            if (earlyOnset) {
              this.markOnsetAccuracyWithSideEffects(
                closestInBucket,
                MidiEventState.EARLY_ONSET,
                timestamp,
                false,
                midiNote,
              );
            } else if (lateOnset) {
              this.markOnsetAccuracyWithSideEffects(
                closestInBucket,
                MidiEventState.LATE_ONSET,
                timestamp,
                false,
                midiNote,
              );
            } else {
              this.markOnsetAccuracyWithSideEffects(
                closestInBucket,
                MidiEventState.HIT,
                timestamp,
                true,
                midiNote,
              );
            }
          } else if (
            closestInBucket &&
            !isErrorState(closestInBucket.OnsetState) &&
            closestInBucket.OffsetState === MidiEventState.UNSET
          ) {
            this.markOnsetAccuracyWithSideEffects(
              closestInBucket,
              MidiEventState.WRONG_NOTE,
              timestamp,
              false,
              midiNote,
            );
          }
        }
      } else {
        restMatches.sort(
          (rest1, rest2) =>
            Math.abs(midiNote - rest1.Timestamp) - Math.abs(midiNote - rest2.Timestamp),
        );

        let closestRestMatch: RestEvent | undefined =
          restMatches.length > 0 ? restMatches[0] : undefined;
        for (const rest of restMatches) {
          if (
            rest.State === MidiEventState.UNSET &&
            !rest.Hidden &&
            (!closestRestMatch ||
              (closestRestMatch &&
                Math.abs(timestamp - rest.Timestamp) <
                  Math.abs(timestamp - closestRestMatch.Timestamp)))
          ) {
            closestRestMatch = rest;
          }
        }
        if (closestRestMatch && !closestRestMatch.Hidden) {
          this.markRestAccuracyWithSideEffects(closestRestMatch, midiNote, timestamp);
          if (closestRestMatch?.Hand === HANDEDNESS.LEFT) {
            closestRestMatch.LhSnapshotPhraseAccuracy = this.calcLHAccuracy();
          }
          if (closestRestMatch?.Hand === HANDEDNESS.RIGHT) {
            closestRestMatch.RhSnapshotPhraseAccuracy = this.calcRHAccuracy();
          }
          closestRestMatch.SnapshotPhraseAccuracy = this.calcAccuracy();
          closestRestMatch.SnapshotBPM = this.timeKeeper.getBpm();
          closestRestMatch.FreePlay = this.freePlayMode;
          closestRestMatch.OffsetTimestamp = timestamp;
        }
      }
    }
    const currentPhrase = this.getNBeforePhrase(0);
    if (currentPhrase) {
      currentPhrase.pushEvent({
        midiEvent: MidiEventType.NOTE_ON,
        midiNote,
        timestamp: timestamp - currentPhrase.getStartTimestamp(),
      });
      currentPhrase.pushRHRunningAccuracy(this.calcAccuracy());
      if (closestNoteMatch) {
        closestNoteMatch.SnapshotPhraseAccuracy = this.calcAccuracy();
        closestNoteMatch.FreePlay = this.freePlayMode;
        if (closestNoteMatch.Hand === HANDEDNESS.LEFT) {
          closestNoteMatch.LhSnapshotPhraseAccuracy = this.calcLHAccuracy();
        }
        if (closestNoteMatch.Hand === HANDEDNESS.RIGHT) {
          closestNoteMatch.RhSnapshotPhraseAccuracy = this.calcRHAccuracy();
        }
      }
      if (currentPhrase.getHasLeftHandedNotes()) {
        currentPhrase.pushLHRunningAccuracy(this.calcLHAccuracy());
      }
      if (currentPhrase.getHasRightHandedNotes()) {
        currentPhrase.pushRHRunningAccuracy(this.calcRHAccuracy());
      }
    }
  }

  handleNoteOffEvent(midiNote: number, inputTiming?: EventInputTiming) {
    const timestamp = this.getLatencyAdjustedTimestamp(inputTiming);
    const isAudioRecognitionInput =
      inputTiming?.source === 'audio-recognition' ||
      this.activeOnsetInputSources[midiNote] === 'audio-recognition';
    // if (this.timeKeeper.isPaused()) {
    //   return;
    // }
    const currentPhrase = this.getNBeforePhrase(0);
    if (currentPhrase) {
      // might not be necessary. I think we're going to go with the event stream over raw midi. It contains the midi info.
      // This may be deleted in the future. Not sure yet.
      currentPhrase.pushEvent({
        midiEvent: MidiEventType.NOTE_OFF,
        midiNote,
        timestamp: timestamp - currentPhrase.getStartTimestamp(),
      });
    }

    if (!this.errorRecognitionActive.left && !this.errorRecognitionActive.right) {
      logger.debug('error rec turned off - returning from note off');
      return;
    }
    // Offset events are much easier to handle than onset events!
    // We already matched the onset event earlier and kept track of it.
    // All we have to do now is re-color the note if an offset event was out of range.
    const match = this.activeOnsetEvents[midiNote];
    if (!match) {
      return;
    }
    match.OffsetTimestamp = timestamp;
    match.SnapshotBPM = this.timeKeeper.getBpm();
    match.FreePlay = this.freePlayMode;
    if (match?.Hand === HANDEDNESS.LEFT && !this.errorRecognitionActive.left) {
      logger.debug('left hand error rec turned off - returning from note off');
      return;
    }
    if (match?.Hand === HANDEDNESS.RIGHT && !this.errorRecognitionActive.right) {
      logger.debug('right hand error rec turned off - returning from note off');
      return;
    }
    if (match instanceof AdditionalNoteEvent) {
      return;
    }

    if (match != null && match.OffsetState === MidiEventState.UNSET) {
      // make sure to set bpm on notes that were missed
      match.SnapshotBPM = this.timeKeeper.getBpm();
      match.FreePlay = this.freePlayMode;
      const timingOffsets = match.NoteOffTimingOffsets;

      if (match.OnsetState === MidiEventState.WRONG_NOTE) {
        this.setOffsetAccuracy(match, MidiEventState.WRONG_NOTE, timestamp); // if onset is wrong note, offset is as well
      } else if (
        timestamp >= timingOffsets.correctStartTimestamp &&
        timestamp <= timingOffsets.correctEndTimestamp &&
        match.OnsetState === MidiEventState.HIT &&
        // additional note has been moved to its own event, so check if the additional note is null
        match.AdditionalNote === null
      ) {
        // WOOHOO, they hit the correct zone
        if (!isSemiErrorState(match.OnsetState)) {
          this.markOffsetAccuracyWithSideEffects(match, MidiEventState.HIT, timestamp);
        }
      } else if (
        isAudioRecognitionInput &&
        timestamp < timingOffsets.correctStartTimestamp &&
        match.OnsetState === MidiEventState.HIT &&
        match.AdditionalNote === null
      ) {
        // Once an acoustic piano tone falls below the recognition floor, the
        // microphone cannot tell whether the key is still physically held.
        // Preserve the first-disappearance timestamp for profiling, but do
        // not turn natural decay after a clean onset into an early-release
        // error. Late audible sustain remains gradeable below, while hardware
        // MIDI keeps its mechanical note-off timing zones.
        this.markOffsetAccuracyWithSideEffects(match, MidiEventState.HIT, timestamp);
      } else if (timestamp < timingOffsets.correctStartTimestamp) {
        // Released before the green zone opens -> early (yellow). Previously this
        // only fired at <= mistimedStartTimestamp, leaving a dead zone
        // (mistimedStart..correctStart) where an early release hit no branch and
        // kept its green onset state. Grading the whole pre-green span yellow
        // removes that leniency; the audio-recognition natural-decay exemption
        // above still short-circuits acoustic input before we get here.
        this.markOffsetAccuracyWithSideEffects(match, MidiEventState.EARLY_OFFSET, timestamp);
      } else if (timestamp >= timingOffsets.correctEndTimestamp) {
        this.markOffsetAccuracyWithSideEffects(match, MidiEventState.LATE_OFFSET, timestamp);
      }

      // Now that we've processed the note off event, we're no longer actively tracking this note,
      // and can unset it in the events array.
      this.activeOnsetEvents[midiNote] = null;
      this.activeOnsetInputSources[midiNote] = null;
    } else if (isAudioRecognitionInput) {
      // A delayed acoustic release can arrive after the score timeout has
      // already graded it. Clear only the audio lifecycle slot so the release
      // cannot leak into a later same-pitch score event.
      this.activeOnsetEvents[midiNote] = null;
      this.activeOnsetInputSources[midiNote] = null;
    }
  }

  checkAccuracyNoteHand(note: BaseNoteEvent | null) {
    if (note != null) {
      if (note.Hand === HANDEDNESS.RIGHT && !this.errorRecognitionActive.right) return false;
      if (note.Hand === HANDEDNESS.LEFT && !this.errorRecognitionActive.left) return false;
    }
    return true;
  }

  // DO NOT USE: This does not work for all cases. It was vibe coded and its a bit of an overkill (and too restrictive)
  // for what it's being used for. Sight reading doesn't use this anymore. We may find a use for it in the future.
  /**
   * Sight-reading waits for an on-beat note before switching from the blinking cursor to playback.
   * The streamed {@link handleNoteOnEvent} is inactive until then (main TimeKeeper paused), so we
   * mirror its onset-bucket pitch logic here: if any expected note at `measureTimestamp` falls in
   * the mistimed onset window and matches `midiNote` (per hand filters), return true. If there is
   * at least one such expected note but none match the pitch, return false (would be WRONG_NOTE / red).
   */
  isPitchValidForSightReadingStart(midiNote: number, measureTimestamp: number): boolean {
    if (!this.errorRecognitionActive.left && !this.errorRecognitionActive.right) {
      Logger.debug(
        '[noteOn::isPitchValidForSightReadingStart] Error recognition is off - returning true',
      );
      return true;
    }
    let anyExpectedInMistimedWindow = false;
    let matchedExpectedPitch = false;
    for (
      let i = this.onsetIndex > 0 ? this.onsetIndex - 1 : this.onsetIndex;
      i < this.eventBuckets.length;
      i++
    ) {
      const bucket = this.eventBuckets[i];
      if (!this.isInBucket(measureTimestamp, i)) {
        continue;
      }
      if (measureTimestamp < bucket.minOnsetTimestamp) {
        break;
      }
      for (const noteEvent of bucket.notes) {
        if (
          measureTimestamp < noteEvent.NoteOnTimingOffsets.mistimedStartTimestamp ||
          measureTimestamp > noteEvent.NoteOnTimingOffsets.mistimedEndTimestamp
        ) {
          continue;
        }
        if (!this.checkAccuracyNoteHand(noteEvent)) {
          continue;
        }
        anyExpectedInMistimedWindow = true;
        if (noteEvent.MidiNote === midiNote) {
          matchedExpectedPitch = true;
        }
      }
    }
    if (!anyExpectedInMistimedWindow) {
      return true;
    }
    return matchedExpectedPitch;
  }

  // Should be called on a set interval. Looks for missed notes and colors them yellow/red.
  update() {
    if (!this.errorRecognitionActive.left && !this.errorRecognitionActive.right) {
      return;
    }
    // Don't update if we re-wound and are counting back in.
    const timestamp = this.getLatencyAdjustedTimestamp();
    if (
      timestamp < this.lastProcessedTimestamp ||
      this.missedNotesIndex >= this.eventBuckets.length
    ) {
      return;
    }

    this.lastProcessedTimestamp = timestamp;

    // Look for notes in buckets that should be colored yellow or red.
    for (
      let i = this.onsetIndex;
      i < this.eventBuckets.length && this.isInBucket(timestamp, i);
      i++
    ) {
      const bucket = this.eventBuckets[i];

      // Skip buckets outside loop bounds (exclusive end — notes AT the
      // bracket end are the first out-of-bracket notes and must not be colored)
      if (this.loopStartTimestamp !== null && this.loopEndTimestamp !== null) {
        if (
          bucket.timestamp < this.loopStartTimestamp ||
          bucket.timestamp >= this.loopEndTimestamp
        ) {
          continue;
        }
      }

      bucket.notes.forEach((note) => {
        if (!this.checkAccuracyNoteHand(note)) return;

        // If a note on event has been received for this note, ignore it.
        if (note.OnsetState != MidiEventState.UNSET) {
          return;
        }

        if (timestamp > note.NoteOnTimingOffsets.mistimedEndTimestamp) {
          // Mistimed window was missed, color error
          console.log(
            `[LOOP-DBG] EventStream.update marking note as NEVER_PLAYED at timestamp=${timestamp} noteTimestamp=${note.Timestamp} onsetIndex=${this.missedNotesIndex} loopStart=${this.loopStartTimestamp} loopEnd=${this.loopEndTimestamp}`,
          );
          this.markOnsetAccuracyWithSideEffects(note, MidiEventState.NEVER_PLAYED, -1, false, null);

          note.OffsetState = MidiEventState.NEVER_PLAYED;
        } else if (timestamp > note.NoteOnTimingOffsets.correctEndTimestamp) {
          // Correct window was missed, color mistimed
        }
      });
    }

    // Look for held-down notes whose offset buckets were missed.
    this.activeOnsetEvents.forEach((note, midiNote) => {
      if (note instanceof AdditionalNoteEvent) {
        return;
      }
      if (!this.checkAccuracyNoteHand(note)) return;

      const isAudioRecognitionInput =
        this.activeOnsetInputSources[midiNote] === 'audio-recognition';
      const releaseTimeoutTimestamp =
        note?.NoteOffTimingOffsets.mistimedEndTimestamp ?? Number.POSITIVE_INFINITY;
      const effectiveReleaseTimeoutTimestamp = isAudioRecognitionInput
        ? Math.max(
            releaseTimeoutTimestamp,
            note?.NoteOffTimingOffsets.correctEndTimestamp ?? releaseTimeoutTimestamp,
          ) + getInputLatencyMeasureOffset(this.timeKeeper, AUDIO_RELEASE_EVENT_TIMEOUT_MS)
        : releaseTimeoutTimestamp;

      if (
        note &&
        note.OffsetState == MidiEventState.UNSET &&
        effectiveReleaseTimeoutTimestamp < timestamp
      ) {
        if (note.OnsetState === MidiEventState.UNSET) {
          this.markOffsetAccuracyWithSideEffects(note, MidiEventState.NEVER_PLAYED, null);
        } else if (note.OnsetState === MidiEventState.HIT) {
          this.markOffsetAccuracyWithSideEffects(note, MidiEventState.LATE_OFFSET, null);
        }

        if (isAudioRecognitionInput) {
          this.activeOnsetEvents[midiNote] = null;
          this.activeOnsetInputSources[midiNote] = null;
        }
      }
    });

    // Progressive tie green: while a folded tie is being held correctly (struck
    // HIT, not yet released), green each of its heads as the cursor reaches that
    // head's timestamp. Stateless subset paint -> idempotent per frame and
    // loop/rewind-safe. Only fires for tie-starts carrying >1 head; single notes
    // and untied notes are untouched. On a premature release / wrong note the
    // note leaves this held-and-HIT state and the offset/onset paths repaint all
    // heads yellow/red, so this pass stops greening it.
    this.activeOnsetEvents.forEach((note) => {
      if (!(note instanceof NoteEvent)) return;
      const heads = note.HeadTimestamps;
      if (!heads || heads.length <= 1) return;
      if (note.OnsetState !== MidiEventState.HIT || note.OffsetState !== MidiEventState.UNSET) {
        return;
      }
      // A wrong/extra note played during the hold penalises this note and paints
      // it yellow/red WITHOUT changing OnsetState/OffsetState. The signal is
      // note.AdditionalNote (set on the first extra note) - note.ErrorScore is
      // NOT yet set on that first extra note because serErrorScore runs before
      // AdditionalNote is assigned. Without this guard the per-frame green repaint
      // reverts that yellow to green - the tie-only flicker bug. Once penalised,
      // stop greening it, exactly as an untied note keeps its yellow/red (the
      // mistake is a permanent, honest record).
      if (note.AdditionalNote !== null || note.ErrorScore > 0) return;
      if (!this.checkAccuracyNoteHand(note)) return;
      this.paintTieGreenSubset(note, timestamp);
    });

    // Advance the onset index if we've exceeded the max onset for this bucket.
    while (
      this.eventBuckets[this.onsetIndex] &&
      timestamp > this.eventBuckets[this.onsetIndex].maxOnsetTimestamp
    ) {
      const bucket = this.eventBuckets[this.onsetIndex];

      // Skip marking notes as missed for buckets outside loop bounds
      const isOutsideLoopBounds =
        this.loopStartTimestamp !== null &&
        this.loopEndTimestamp !== null &&
        (bucket.timestamp < this.loopStartTimestamp || bucket.timestamp >= this.loopEndTimestamp);

      if (isOutsideLoopBounds) {
        this.onsetIndex++;
        continue;
      }

      Logger.info(
        '[EventStream.update] Advancing onsetIndex from',
        this.onsetIndex,
        '- timestamp:',
        timestamp,
        'maxOnset:',
        bucket.maxOnsetTimestamp,
        'bucketTimestamp:',
        bucket.timestamp,
      );
      bucket.notes.forEach((note) => {
        if (!this.checkAccuracyNoteHand(note)) return;

        if (note.OnsetState === MidiEventState.UNSET) {
          Logger.info(
            '[EventStream.update] Marking note as NEVER_PLAYED - midiNote:',
            note.MidiNote,
            'noteTimestamp:',
            note.Timestamp,
          );
          this.markOnsetAccuracyWithSideEffects(note, MidiEventState.NEVER_PLAYED, -1, false, null);
          this.setOffsetAccuracy(note, MidiEventState.NEVER_PLAYED);
        }
      });
      this.onsetIndex++;
    }
  }

  private calculateTiedNoteLength(note: Note): number {
    if (note.NoteTie.Notes[0] == note) {
      return note.NoteTie.Duration.RealValue;
    }
    return -1;
  }

  // Prerendered-path tie merging is done inline in addEvents2(): the prerenderer
  // (musicxml-tagging) tags each tied note with noteTie.isTieStart + tieDuration,
  // so addEvents2 folds continuations into the tie-start's held expected-note.
  // This mirrors calculateTiedNoteLength() above for the live-OSMD path.

  private calculateCorrectNoteOffMin(noteDuration: number) {
    if (noteDuration <= 1.0 / 4.0) {
      return noteDuration * 0.5;
    }
    // If duration is between a quarter note and half note, use this block.
    else if (noteDuration > 1.0 / 4.0 && noteDuration < 2.0 / 4.0) {
      const nRange = 2.0 / 4.0 - 1.0 / 4.0;
      // Scale from 0.5 to 0.625 of the note, on a nearly linear curve
      let nScale = 1.0 - (2.0 / 4.0 - noteDuration) / nRange;
      // nScale is normalized from 0..1; putting that on a curve nScale^0.6
      // lets a noteDuration=1.5 have minimum offset of just under 1 beat.
      // Adjust the following line to adjust how "aggressive" scaling between 1 and 2 is.
      // Use graphtoy.com to visualize the curve you're adjusting.
      nScale = Math.pow(nScale, 0.6);
      return 1.0 / 8.0 + 0.1875 * nScale;
    } else {
      return noteDuration - 1.0 / 4.0 + 1.0 / 16.0;
    }
  }

  protected pulseMatchedNote(note: NoteEvent, colorClass: string, _noteTimestamp?: number) {
    const vgNote = note.Graphics[0] as unknown as { staveNote: { id: string } } | undefined;
    if (!vgNote?.staveNote?.id) {
      Logger.error('note has no rendered graphic!!');
      return;
    }
    // const staveNote = vgNote.getSVGGElement()
    const staveNote = document.getElementById(vgNote.staveNote.id);
    // Notes with staves will report the wrong height; we have to grab a descendant
    // which is ONLY the note head so we calculate pulse height offset correctly.
    const noteHead = staveNote?.querySelector('.vf-notehead');

    if (noteHead == null) {
      Logger.error('note has no note head!!');
      return;
    }
    let colorClassCSSPostfix = 'correct-green';
    if (
      colorClass === ERROR_REC_CLASS.CORRECT &&
      this.colorblindType === COLORBLIND_TYPE.RED_GREEN
    ) {
      colorClassCSSPostfix = 'correct-blue';
    } else if (
      colorClass === ERROR_REC_CLASS.CORRECT &&
      this.colorblindType === COLORBLIND_TYPE.FULL
    ) {
      colorClassCSSPostfix = 'correct-white';
    }

    // Get the bounding rect of the note head relative to the document body.
    // We will append a pulse to the document, and position it over the note.
    const boundingRect = noteHead.getBoundingClientRect();

    // Create the pulse.
    const pulse = document.createElement('div');
    pulse.classList.add('pulsating-circle', colorClassCSSPostfix);
    document.body.appendChild(pulse);

    // Position the pulse.
    pulse.style.left = boundingRect.x + boundingRect.width / 2 + 'px';
    pulse.style.top = window.scrollY + boundingRect.y + boundingRect.height / 2 + 'px';

    // The animation is decorative; once it completes, retaining its DOM node
    // only grows document.body for the lifetime of a practice session. The
    // timeout also covers reduced-motion/stylesheet failures where no
    // animationend event is emitted.
    const removePulse = () => pulse.remove();
    pulse.addEventListener('animationend', removePulse, { once: true });
    window.setTimeout(removePulse, 1000);
  }

  private isOnsetTimeInBucket(timestamp: number, bucketIndex: number) {
    const minOnsetTimestamp = this.eventBuckets[bucketIndex].minOnsetTimestamp;
    const maxOnsetTimestamp = this.eventBuckets[bucketIndex].maxOnsetTimestamp;
    // logger.info("minOnsetTimestamp = " + minOnsetTimestamp)
    // logger.info("maxOnsetTimestamp = " + maxOnsetTimestamp )
    // logger.info("timestamp = " + timestamp)
    // logger.info( timestamp > minOnsetTimestamp
    //     && timestamp < maxOnsetTimestamp)
    return timestamp > minOnsetTimestamp && timestamp < maxOnsetTimestamp;
  }

  private isInBucket(timestamp: number, bucketIndex: number) {
    const minOnsetTimestamp = this.eventBuckets[bucketIndex].minOnsetTimestamp;
    const maxOffsetTimestamp = this.eventBuckets[bucketIndex].maxOffsetTimestamp;
    // logger.info("minOnsetTimestamp = " + minOnsetTimestamp)
    // logger.info("maxOnsetTimestamp = " + maxOnsetTimestamp )
    // logger.info("timestamp = " + timestamp)
    // logger.info( timestamp > minOnsetTimestamp
    //     && timestamp < maxOnsetTimestamp)
    return timestamp > minOnsetTimestamp && timestamp < maxOffsetTimestamp;
  }

  public clearErrors() {
    for (const eventBucket of this.eventBuckets) {
      eventBucket.notes.forEach((note) => {
        resetNote(note.Graphics);
        note.OffsetState = MidiEventState.UNSET;
        note.OnsetState = MidiEventState.UNSET;
      });
    }
    this.accuracyQueue = [];
  }

  /**
   * Reset OnsetState and OffsetState to UNSET for all notes whose bucket
   * timestamp falls within [startTimestamp, endTimestamp]. This allows the
   * event stream to re-evaluate these notes on the next loop pass without
   * affecting their visual coloring (which is managed separately).
   */
  public resetNoteStatesInRange(startTimestamp: number, endTimestamp: number): void {
    for (const eventBucket of this.eventBuckets) {
      const bucketIsInRange =
        eventBucket.timestamp >= startTimestamp && eventBucket.timestamp <= endTimestamp;

      if (!bucketIsInRange) continue;

      eventBucket.notes.forEach((note) => {
        note.OnsetState = MidiEventState.UNSET;
        note.OffsetState = MidiEventState.UNSET;
        note.ErrorScore = 0;
        // Clear any extra/wrong note recorded against this note. Without this,
        // a single mistimed/extra note leaves AdditionalNote populated, which
        // (a) blocks re-grading (the `AdditionalNote === null` guard in
        // markOnsetAccuracyWithSideEffects) and (b) keeps the bucket flagged
        // as having an additional note forever — permanently failing the
        // perfect-phrase accuracy poll so a broken streak could never recover.
        note.AdditionalNote = null;
      });

      eventBucket.rests.forEach((rest) => {
        rest.State = MidiEventState.UNSET;
      });

      // Drop stale extra-note events so each loop pass re-evaluates from clean.
      eventBucket.additinalNotes = [];
    }
  }

  protected makeNoteSemiError = (
    graphics: PrerenderedGraphics[],
    colorblindType: COLORBLIND_TYPE | null,
    _noteTimestamp?: number,
    _headRank?: number,
    _chordSize?: number,
  ) => makeNoteSemiError(graphics, colorblindType);
  //protected this.makeNoteRed(graphics: PrerenderedGraphics[]) {}
  protected makeNoteCorrect = (
    graphics: PrerenderedGraphics[],
    colorblindType: COLORBLIND_TYPE | null,
    _noteTimestamp?: number,
    _headRank?: number,
    _chordSize?: number,
  ) => makeNoteCorrect(graphics, colorblindType);
  protected makeNoteError = (
    graphics: PrerenderedGraphics[],
    colorblindType: COLORBLIND_TYPE | null,
    _noteTimestamp?: number,
    _headRank?: number,
    _chordSize?: number,
  ) => makeNoteError(graphics, colorblindType);
}
